import { Validators, AbstractControl, ValidationErrors } from '@angular/forms';

export interface FormField {
  name: string;
  label: string;
  type:
    | 'text'
    | 'number'
    | 'email'
    | 'tel'
    | 'textarea'
    | 'select'
    | 'checkbox'
    | 'first_registration_dropdown'
    | 'registered_until_dropdown'
    | 'date'
    | 'location'
    | 'vehicle-selector'
    | 'motorcycle-brand-selector';
  validators?: any[];
  options?: { value: any; label: string }[];
  placeholder?: string;
  hint?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  dependsOn?: string;
  showWhen?: any;
  disabled?: boolean;
  value?: any;
  pattern?: string;
  allowCustomInput?: boolean;
}

export function integerOnlyValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  if (value === null || value === '') return null;
  if (Number.isInteger(Number(value))) return null;

  return { notInteger: true };
}

export function vehicleValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  if (!value) return { required: true };

  if (typeof value === 'object') {
    if (!value.brand || !value.model) {
      return { incompleteVehicle: true };
    }
    if (value.brand.trim().length < 2 || value.model.trim().length < 2) {
      return { vehicleTooShort: true };
    }
  }

  return null;
}

export function dateFieldValidator(
  config: {
    yearRange: { start: number; end: number };
    maxDate: Date;
  }
) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;
    const regex = /^(0[1-9]|1[0-2])\/\d{4}$/;
    if (!regex.test(value)) {
      return { invalidFormat: true };
    }

    const [month, year] = value.split('/').map(Number);
    if (month < 1 || month > 12) {
      return { invalidMonth: true };
    }

    if (year < config.yearRange.start || year > config.yearRange.end) {
      return { invalidYear: true };
    }

    const selectedDate = new Date(year, month - 1, 1);
    if (selectedDate > config.maxDate) {
      return { futureDate: true };
    }
    return null;
  };
}

export function motorcycleBrandValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  if (!value) return { required: false };

  if (typeof value === 'string' && value.trim().length < 2) {
    return { brandTooShort: true };
  }

  return null;
}

export function maxDateTodayValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = control.value;

  if (!value) return null;

  const selectedDate = new Date(value);
  const today = new Date();

  selectedDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (selectedDate > today) {
    return { futureDate: true };
  }

  return null;
}

export interface CategoryFormConfig {
  [subcategory: string]: FormField[];
}

const baseFields: FormField[] = [
  {
    name: 'title',
    label: 'Title',
    type: 'text',
    required: true,
    validators: [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(255),
    ],
    placeholder: 'Enter a descriptive title for your listing',
  },
  {
    name: 'description',
    label: 'Description',
    type: 'textarea',
    required: true,
    validators: [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(5000),
    ],
    rows: 6,
    placeholder: 'Provide a detailed description...',
  },
  {
    name: 'location',
    label: 'Location',
    type: 'location',
    validators: [Validators.maxLength(255)],
    placeholder: 'Start typing a city name...',
  },
];

const basePriceFields: FormField[] = [
  {
    name: 'price',
    label: 'Price',
    type: 'number',
    required: true,
    validators: [
      Validators.required,
      Validators.min(1),
      Validators.max(100000000),
    ],
    step: 0.01,
    placeholder: '0.00',
  },
  {
    name: 'currency',
    label: 'Currency',
    type: 'select',
    required: true,
    validators: [Validators.required],
    options: [{ value: 'EUR', label: 'EUR (€)' }],
  },
];

export const vehicleFormConfigs: CategoryFormConfig = {
  automobile: [
    ...baseFields,
    {
      name: 'vehicle',
      label: 'Vehicle',
      type: 'vehicle-selector',
      required: true,
      validators: [Validators.required, vehicleValidator],
      placeholder: 'Select your vehicle brand and model',
      allowCustomInput: true,
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'electric', label: 'Electric' },
        { value: 'lpg', label: 'LPG' },
        { value: 'cng', label: 'CNG' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'fuel_consumption_2',
      label: 'Fuel Consumption (L/100km)',
      type: 'number',
      step: 0.1,
      validators: [
        Validators.min(0),
        Validators.max(50),
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'automatic', label: 'Automatic' },
        { value: 'manual', label: 'Manual' },
        { value: 'semi_automatic', label: 'Semi-automatic' },
      ],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [dateFieldValidator({
        yearRange: { start: 1950, end: new Date().getFullYear() },
        maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      })],
    },
    {
      name: 'registered_until',
      label: 'Registered Until',
      type: 'registered_until_dropdown',
      validators: [dateFieldValidator({
        yearRange: {
          start: new Date().getFullYear(),
          end: new Date().getFullYear() + 1,
        },
        maxDate: new Date(
          new Date().getFullYear() + 1,
          new Date().getMonth(),
          1
        ),
      })],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (L)',
      type: 'number',
      step: 0.1,
      required: true,
      validators: [Validators.required, Validators.min(1), Validators.max(90)],
    },
    {
      name: 'cm3',
      label: 'Engine size (cm³)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(7355),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'body_type',
      label: 'Body Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'sedan', label: 'Sedan' },
        { value: 'hatchback', label: 'Hatchback' },
        { value: 'suv', label: 'SUV' },
        { value: 'coupe', label: 'Coupe' },
        { value: 'convertible', label: 'Convertible' },
        { value: 'wagon', label: 'Wagon' },
        { value: 'pickup', label: 'Pickup' },
        { value: 'van', label: 'Van' },
        { value: 'minivan', label: 'Minivan' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'drive_type',
      label: 'Drive Type',
      type: 'select',
      options: [
        { value: 'fwd', label: 'Front-wheel Drive (FWD)' },
        { value: 'rwd', label: 'Rear-wheel Drive (RWD)' },
        { value: 'awd', label: 'All-wheel Drive (AWD)' },
        { value: '4wd', label: 'Four-wheel Drive (4WD)' },
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'interior_color',
      label: 'Interior Color',
      type: 'select',
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'beige', label: 'Beige' },
        { value: 'brown', label: 'Brown' },
        { value: 'red', label: 'Red' },
        { value: 'blue', label: 'Blue' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'doors',
      label: 'Number of Doors',
      type: 'select',
      options: [
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
      ],
    },
    {
      name: 'seats',
      label: 'Number of Seats',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(9),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'euro_emission_class',
      label: 'Euro Emission Class',
      type: 'select',
      options: [
        { value: 1, label: 'Euro 1' },
        { value: 2, label: 'Euro 2' },
        { value: 3, label: 'Euro 3' },
        { value: 4, label: 'Euro 4' },
        { value: 5, label: 'Euro 5' },
        { value: 6, label: 'Euro 6' },
        { value: 7, label: 'Euro 7' },
      ],
    },
    {
      name: 'owners_count',
      label: 'Number of Owners',
      type: 'select',
      options: [
        { value: 1, label: 'No Previous Owner' },
        { value: 2, label: 'First Owner' },
        { value: 3, label: 'Second Owner' },
        { value: 4, label: 'Third Owner' },
        { value: 5, label: 'Fourth Owner or More' },
      ],
    },
    {
      name: 'tank_capacity',
      label: 'Tank Capacity (L)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'cylinders',
      label: 'Number of Cylinders',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(16),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'damaged', label: 'Damaged' },
      ],
    },
    {
      name: 'smoking_history',
      label: 'Smoking History',
      type: 'select',
      options: [
        { value: 'smoker', label: 'Smoker' },
        { value: 'non-smoker', label: 'Non-Smoker' },
      ],
    },
    {
      name: 'service_book',
      label: 'Service Book',
      type: 'select',
      options: [
        { value: 'available', label: 'Available' },
        { value: 'not_available', label: 'Not Available' },
        { value: 'partial', label: 'Partial' },
      ],
    },
    {
      name: 'tyres',
      label: 'Tyres',
      type: 'select',
      options: [
        { value: 'summer', label: 'Summer' },
        { value: 'winter', label: 'Winter' },
        { value: 'm+s', label: 'M/S (All-season)' },
        { value: 'summer+winter', label: 'Summer + Winter (Two Sets)' },
      ],
    },
    {
      name: 'exchange',
      label: 'Exchange',
      type: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      name: 'co2',
      label: 'CO2 Emissions (g/km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
  ],

  motorcycle: [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'motorcycle-brand-selector',
      required: true,
      validators: [Validators.required, motorcycleBrandValidator],
      placeholder: 'Choose your motorcycle brand',
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
      placeholder: 'Enter motorcycle model',
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'petrol', label: 'Petrol' },
        { value: 'electric', label: 'Electric' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'fuel_consumption_2',
      label: 'Fuel Consumption (L/100km)',
      type: 'number',
      step: 0.1,
      validators: [
        Validators.min(0),
        Validators.max(50),
      ],
    },
    {
      name: 'tank_capacity',
      label: 'Tank Capacity (L)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'automatic', label: 'Automatic' },
        { value: 'manual', label: 'Manual' },
        { value: 'semi_automatic', label: 'Semi-automatic' },
      ],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [dateFieldValidator({
        yearRange: { start: 1950, end: new Date().getFullYear() },
        maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      })],
    },
    {
      name: 'registered_until',
      label: 'Registered Until',
      type: 'registered_until_dropdown',
      validators: [dateFieldValidator({
        yearRange: {
          start: new Date().getFullYear(),
          end: new Date().getFullYear() + 1,
        },
        maxDate: new Date(
          new Date().getFullYear() + 1,
          new Date().getMonth(),
          1
        ),
      })],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (cc)',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(15000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(7355),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'motorcycle_type',
      label: 'Motorcycle Type',
      required: true,
      validators: [Validators.required],
      type: 'select',
      options: [
        { value: 'chopper_and_cruiser', label: 'Chopper/Cruiser' },
        { value: 'combination_and_sidecar', label: 'Combination/Sidecar' },
        { value: 'dirt_bike', label: 'Dirt Bike' },
        { value: 'enduro_and_touring_enduro', label: 'Enduro/Touring Enduro' },
        { value: 'lightweight_motorcycle_and_motorbike', label: 'Lightweight Motorcycle/Motorbike' },
        { value: 'motor_assisted_bicycle_and_small_moped', label: 'Motor-assisted Bicycle/Small Moped' },
        { value: 'motorcycle', label: 'Motorcycle' },
        { value: 'naked_bike', label: 'Naked Bike' },
        { value: 'pocketbike', label: 'Pocketbike' },
        { value: 'quad', label: 'Quad' },
        { value: 'racing', label: 'Racing' },
        { value: 'roadster', label: 'Roadster' },
        { value: 'rally_and_cross', label: 'Rally/Cross' },
        { value: 'scooter', label: 'Scooter' },
        { value: 'sports_and_super_sports_bike', label: 'Sports/Super Sports Bike' },
        { value: 'sport_touring_motorcycle', label: 'Sport Touring Motorcycles' },
        { value: 'streetfighter', label: 'Streetfighter' },
        { value: 'super_moto', label: 'Super Moto' },
        { value: 'tourer', label: 'Tourer' },
        { value: 'trike', label: 'Trike' },
        { value: 'other_motorbike', label: 'Other' },
      ],
    },
    {
      name: 'cylinders',
      label: 'Number of Cylinders',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(16),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'cooling',
      label: 'Cooling System',
      type: 'select',
      options: [
        { value: 'air', label: 'Air Cooled' },
        { value: 'liquid', label: 'Liquid Cooled' },
        { value: 'oil', label: 'Oil Cooled' },
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'damaged', label: 'Damaged' },
      ],
    },
    {
      name: 'owners_count',
      label: 'Number of Owners',
      type: 'select',
      options: [
        { value: 1, label: 'No Previous Owner' },
        { value: 2, label: 'First Owner' },
        { value: 3, label: 'Second Owner' },
        { value: 4, label: 'Third Owner' },
        { value: 5, label: 'Fourth Owner or More' },
      ],
    },
    {
      name: 'service_book',
      label: 'Service Book',
      type: 'select',
      options: [
        { value: 'available', label: 'Available' },
        { value: 'not_available', label: 'Not Available' },
        { value: 'partial', label: 'Partial' },
      ],
    },
  ],

  'commercial-vehicles': [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'electric', label: 'Electric' },
        { value: 'lpg', label: 'LPG' },
        { value: 'cng', label: 'CNG' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'fuel_consumption_2',
      label: 'Fuel Consumption (L/100km)',
      type: 'number',
      step: 0.1,
      validators: [
        Validators.min(0),
        Validators.max(50),
      ],
    },
    {
      name: 'tank_capacity',
      label: 'Tank Capacity (L)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'automatic', label: 'Automatic' },
        { value: 'manual', label: 'Manual' },
        { value: 'semi_automatic', label: 'Semi-automatic' },
      ],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [dateFieldValidator({
        yearRange: { start: 1950, end: new Date().getFullYear() },
        maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      })],
    },
    {
      name: 'registered_until',
      label: 'Registered Until',
      type: 'registered_until_dropdown',
      validators: [dateFieldValidator({
        yearRange: {
          start: new Date().getFullYear(),
          end: new Date().getFullYear() + 1,
        },
        maxDate: new Date(
          new Date().getFullYear() + 1,
          new Date().getMonth(),
          1
        ),
      })],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (L)',
      type: 'number',
      step: 0.1,
      required: true,
      validators: [Validators.required, Validators.min(0.1), Validators.max(90)],
    },
    {
      name: 'cm3',
      label: 'Engine size (cm³)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(7355),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'vehicle_type',
      label: 'Vehicle Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'truck', label: 'Truck' },
        { value: 'van', label: 'Van' },
        { value: 'bus', label: 'Bus' },
        { value: 'trailer', label: 'Trailer' },
        { value: 'semi_truck', label: 'Semi Truck' },
        { value: 'pickup_truck', label: 'Pickup Truck' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'interior_color',
      label: 'Interior Color',
      type: 'select',
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'beige', label: 'Beige' },
        { value: 'brown', label: 'Brown' },
        { value: 'red', label: 'Red' },
        { value: 'blue', label: 'Blue' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'doors',
      label: 'Number of Doors',
      type: 'select',
      options: [
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
      ],
    },
    {
      name: 'seats',
      label: 'Number of Seats',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(9),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'euro_emission_class',
      label: 'Euro Emission Class',
      type: 'select',
      options: [
        { value: 1, label: 'Euro 1' },
        { value: 2, label: 'Euro 2' },
        { value: 3, label: 'Euro 3' },
        { value: 4, label: 'Euro 4' },
        { value: 5, label: 'Euro 5' },
        { value: 6, label: 'Euro 6' },
        { value: 7, label: 'Euro 7' },
      ],
    },
    {
      name: 'owners_count',
      label: 'Number of Previous Owners',
      type: 'select',
      options: [
        { value: 1, label: 'No Previous Owner' },
        { value: 2, label: 'First Owner' },
        { value: 3, label: 'Second Owner' },
        { value: 4, label: 'Third Owner' },
        { value: 5, label: 'Fourth Owner or More' },
      ],
    },
    {
      name: 'payload_capacity',
      label: 'Payload Capacity (kg)',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      validators: [
        Validators.min(1),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'axles_count',
      label: 'Number of Axles',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      validators: [
        Validators.min(2),
        Validators.max(12),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'damaged', label: 'Damaged' },
      ],
    },
    {
      name: 'co2',
      label: 'CO2 Emissions (g/km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'cylinders',
      label: 'Number of Cylinders',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(16),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'smoking_history',
      label: 'Smoking History',
      type: 'select',
      options: [
        { value: 'smoker', label: 'Smoker' },
        { value: 'non-smoker', label: 'Non-Smoker' },
      ],
    },
    {
      name: 'service_book',
      label: 'Service Book',
      type: 'select',
      options: [
        { value: 'available', label: 'Available' },
        { value: 'not_available', label: 'Not Available' },
        { value: 'partial', label: 'Partial' },
      ],
    },
  ],

  oldtimer: [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1900),
        Validators.max(1995),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'fuel_consumption_2',
      label: 'Fuel Consumption (L/100km)',
      type: 'number',
      step: 0.1,
      validators: [
        Validators.min(0),
        Validators.max(50),
      ],
    },
    {
      name: 'tank_capacity',
      label: 'Tank Capacity (L)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'automatic', label: 'Automatic' },
        { value: 'manual', label: 'Manual' },
      ],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [dateFieldValidator({
        yearRange: { start: 1950, end: new Date().getFullYear() },
        maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      })],
    },
    {
      name: 'registered_until',
      label: 'Registered Until',
      type: 'registered_until_dropdown',
      validators: [dateFieldValidator({
        yearRange: {
          start: new Date().getFullYear(),
          end: new Date().getFullYear() + 1,
        },
        maxDate: new Date(
          new Date().getFullYear() + 1,
          new Date().getMonth(),
          1
        ),
      })],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (L)',
      type: 'number',
      step: 0.1,
      required: true,
      validators: [Validators.required, Validators.min(1), Validators.max(99.9)],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(7355),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'vehicle_type',
      label: 'Vehicle Type',
      type: 'select',
      options: [
        { value: 'automobile', label: 'Automobile' },
        { value: 'motorcycle', label: 'Motorcycle' },
        { value: 'campers_caravans', label: 'Campers and Caravans' },
        { value: 'commercial', label: 'Commercial' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'drive_type',
      label: 'Drive Type',
      type: 'select',
      options: [
        { value: 'fwd', label: 'Front-wheel Drive (FWD)' },
        { value: 'rwd', label: 'Rear-wheel Drive (RWD)' },
        { value: 'awd', label: 'All-wheel Drive (AWD)' },
        { value: '4wd', label: 'Four-wheel Drive (4WD)' },
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'interior_color',
      label: 'Interior Color',
      type: 'select',
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'beige', label: 'Beige' },
        { value: 'brown', label: 'Brown' },
        { value: 'red', label: 'Red' },
        { value: 'blue', label: 'Blue' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'restored', label: 'Fully Restored' },
        { value: 'original', label: 'Original Condition' },
        { value: 'needs_restoration', label: 'Needs Restoration' },
      ],
    },
    {
      name: 'restoration_level',
      label: 'Restoration Level',
      type: 'select',
      options: [
        { value: 'complete', label: 'Complete Restoration' },
        { value: 'partial', label: 'Partial Restoration' },
        { value: 'original', label: 'Original/Unrestored' },
        { value: 'none', label: 'No Restoration' },
      ],
    },
    {
      name: 'documentation',
      label: 'Documentation',
      type: 'select',
      options: [
        { value: 'complete', label: 'Complete Documentation' },
        { value: 'partial', label: 'Partial Documentation' },
        { value: 'missing', label: 'Missing Documentation' },
      ],
    },
    {
      name: 'co2',
      label: 'CO2 Emissions (g/km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'cylinders',
      label: 'Number of Cylinders',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(16),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'cm3',
      label: 'Engine size (cm³)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'smoking_history',
      label: 'Smoking History',
      type: 'select',
      options: [
        { value: 'smoker', label: 'Smoker' },
        { value: 'non-smoker', label: 'Non-Smoker' },
      ],
    },
    {
      name: 'service_book',
      label: 'Service Book',
      type: 'select',
      options: [
        { value: 'available', label: 'Available' },
        { value: 'not_available', label: 'Not Available' },
        { value: 'partial', label: 'Partial' },
      ],
    },
  ],

  nautical: [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'boat_type',
      label: 'Boat Type',
      type: 'select',
      options: [
        { value: 'sailboat', label: 'Sailboat' },
        { value: 'motorboat', label: 'Motorboat' },
        { value: 'yacht', label: 'Yacht' },
        { value: 'catamaran', label: 'Catamaran' },
        { value: 'jet_ski', label: 'Jet Ski' },
        { value: 'fishing_boat', label: 'Fishing Boat' },
        { value: 'speedboat', label: 'Speedboat' },
        { value: 'pontoon', label: 'Pontoon' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'length_meters',
      label: 'Length (meters)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0.1), Validators.max(150)],
    },
    {
      name: 'beam_meters',
      label: 'Beam Width (meters)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0.1), Validators.max(30)],
    },
    {
      name: 'draft_meters',
      label: 'Draft (meters)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0.1), Validators.max(20)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'dry_weight_kg',
      label: 'Dry Weight (kg)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(500000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'engine_type',
      label: 'Engine Type',
      type: 'select',
      options: [
        { value: 'inboard', label: 'Inboard' },
        { value: 'outboard', label: 'Outboard' },
        { value: 'sterndrive', label: 'Sterndrive' },
        { value: 'jet_drive', label: 'Jet Drive' },
        { value: 'sail_only', label: 'Sail Only' },
        { value: 'electric', label: 'Electric' },
      ],
    },
    {
      name: 'engine_count',
      label: 'Number of Engines',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(10),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'engine_power_hp',
      label: 'Engine Power (HP)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'fuel_type',
      label: 'Fuel Type',
      type: 'select',
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'electric', label: 'Electric' },
        { value: 'none', label: 'None (Sail Only)' },
      ],
    },
    {
      name: 'fuel_consumption_2',
      label: 'Fuel Consumption (L/h)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0), Validators.max(5000)],
    },
    {
      name: 'tank_capacity',
      label: 'Fuel Tank Capacity (L)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'water_tank_capacity',
      label: 'Water Tank Capacity (L)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'cabins',
      label: 'Number of Cabins',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(25),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'berths',
      label: 'Number of Berths',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'seating_capacity',
      label: 'Seating Capacity',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(500),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'needs_work', label: 'Needs Work' },
      ],
    },
    {
      name: 'hull_material',
      label: 'Hull Material',
      type: 'select',
      options: [
        { value: 'fiberglass', label: 'Fiberglass' },
        { value: 'wood', label: 'Wood' },
        { value: 'aluminum', label: 'Aluminum' },
        { value: 'steel', label: 'Steel' },
        { value: 'carbon', label: 'Carbon Fiber' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'registered_until',
      label: 'Registered Until',
      type: 'registered_until_dropdown',
      validators: [dateFieldValidator({
        yearRange: {
          start: new Date().getFullYear(),
          end: new Date().getFullYear() + 1,
        },
        maxDate: new Date(
          new Date().getFullYear() + 1,
          new Date().getMonth(),
          1
        ),
      })],
    },
  ],
  'spare-parts': [
    ...baseFields,
    {
      name: 'part_type',
      label: 'Part Type',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(50)],
      placeholder: 'Enter part type',
    },
    ...basePriceFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'part_number',
      label: 'Part Number',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'compatible_vehicles',
      label: 'Compatible Vehicles',
      type: 'textarea',
      rows: 3,
      placeholder: 'List compatible vehicle models',
    },
    {
      name: 'vehicle_type',
      label: 'Vehicle Type',
      type: 'select',
      options: [
        { value: 'car', label: 'Car' },
        { value: 'motorcycle', label: 'Motorcycle' },
        { value: 'pickup', label: 'Pickup Truck' },
        { value: 'van', label: 'Van' },
        { value: 'scooter', label: 'Scooter' },
        { value: 'bicycle', label: 'Bicycle' },
        { value: 'truck', label: 'Truck' },
        { value: 'semi_truck', label: 'Semi Truck' },
        { value: 'bus', label: 'Bus' },
        { value: 'minibus', label: 'Minibus' },
        { value: 'trailer', label: 'Trailer' },
        { value: 'forklift', label: 'Forklift' },
        { value: 'tractor', label: 'Tractor' },
        { value: 'agricultural_machinery', label: 'Agricultural Machinery' },
        { value: 'quad_atv', label: 'ATV/Quad' },
        { value: 'construction_machinery', label: 'Construction Machinery' },
        { value: 'boat', label: 'Boat' },
        { value: 'jetski', label: 'Jetski' },
        { value: 'snowmobile', label: 'Snowmobile' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'year_from',
      label: 'Compatible Year From',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'year_to',
      label: 'Compatible Year To',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'refurbished', label: 'Refurbished' },
        { value: 'damaged', label: 'Damaged' },
      ],
    },
    {
      name: 'warranty_months',
      label: 'Warranty (months)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(120),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'quantity',
      label: 'Quantity',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'dimensions',
      label: 'Dimensions',
      type: 'text',
      placeholder: 'Example: 30cm x 20cm x 10cm',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'position',
      label: 'Position / Location',
      type: 'select',
      options: [
        { value: 'front', label: 'Front' },
        { value: 'rear', label: 'Rear' },
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
        { value: 'engine', label: 'Engine Area' },
        { value: 'interior', label: 'Interior' },
        { value: 'exterior', label: 'Exterior' },
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'country_of_origin',
      label: 'Country of Origin',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
  ],

  'campers-caravans': [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1950),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'vehicle_type',
      label: 'Vehicle Type',
      type: 'select',
      options: [
        { value: 'motorhome', label: 'Motorhome' },
        { value: 'class_a', label: 'Class A Motorhome' },
        { value: 'class_b', label: 'Class B Camper Van' },
        { value: 'class_c', label: 'Class C Motorhome' },
        { value: 'truck_camper', label: 'Truck Camper' },
        { value: 'camper_van', label: 'Camper Van' },
        { value: 'camper_conversion', label: 'Van / Bus Conversion' },
        { value: 'caravan', label: 'Caravan' },
        { value: 'compact_caravan', label: 'Compact Caravan' },
        { value: 'luxury_caravan', label: 'Luxury Caravan' },
        { value: 'offroad_caravan', label: 'Off-road Caravan' },
        { value: 'travel_trailer', label: 'Travel Trailer' },
        { value: 'fifth_wheel', label: 'Fifth Wheel' },
        { value: 'pop_up', label: 'Pop-up Camper' },
        { value: 'teardrop', label: 'Teardrop Trailer' },
        { value: 'tiny_home_trailer', label: 'Tiny Home Trailer' },
        { value: 'toyhauler', label: 'Toy Hauler' },
        { value: 'expedition_vehicle', label: 'Expedition Vehicle' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'length_meters',
      label: 'Length (meters)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0.1), Validators.max(99.99)],
    },
    {
      name: 'width_meters',
      label: 'Width (meters)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0.1), Validators.max(9.99)],
    },
    {
      name: 'height_meters',
      label: 'Height (meters)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0.1), Validators.max(9.99)],
    },
    {
      name: 'sleeping_capacity',
      label: 'Sleeping Capacity',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(12),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (L)',
      type: 'number',
      step: 0.1,
      required: true,
      validators: [Validators.required, Validators.min(1), Validators.max(90)],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'electric', label: 'Electric' },
        { value: 'lpg', label: 'LPG' },
        { value: 'cng', label: 'CNG' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      pattern: '^[0-9]+$',
      step: 1,
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(7355),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      options: [
        { value: 'automatic', label: 'Automatic' },
        { value: 'manual', label: 'Manual' },
        { value: 'semi_automatic', label: 'Semi-automatic' },
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'damaged', label: 'Damaged' },
      ],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [dateFieldValidator({
        yearRange: { start: 1950, end: new Date().getFullYear() },
        maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      })],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'euro_emission_class',
      label: 'Euro Emission Class',
      type: 'select',
      options: [
        { value: 1, label: 'Euro 1' },
        { value: 2, label: 'Euro 2' },
        { value: 3, label: 'Euro 3' },
        { value: 4, label: 'Euro 4' },
        { value: 5, label: 'Euro 5' },
        { value: 6, label: 'Euro 6' },
        { value: 7, label: 'Euro 7' },
      ],
    },
    {
      name: 'owners_count',
      label: 'Number of Owners',
      type: 'select',
      options: [
        { value: 1, label: 'No Previous Owner' },
        { value: 2, label: 'First Owner' },
        { value: 3, label: 'Second Owner' },
        { value: 4, label: 'Third Owner' },
        { value: 5, label: 'Fourth Owner or More' },
      ],
    },
    {
      name: 'interior_color',
      label: 'Interior Color',
      type: 'select',
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'beige', label: 'Beige' },
        { value: 'brown', label: 'Brown' },
        { value: 'red', label: 'Red' },
        { value: 'blue', label: 'Blue' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'smoking_history',
      label: 'Smoking History',
      type: 'select',
      options: [
        { value: 'smoker', label: 'Smoker' },
        { value: 'non-smoker', label: 'Non-Smoker' },
      ],
    },
    {
      name: 'registered_until',
      label: 'Registered Until',
      type: 'registered_until_dropdown',
      validators: [dateFieldValidator({
        yearRange: {
          start: new Date().getFullYear(),
          end: new Date().getFullYear() + 1,
        },
        maxDate: new Date(
          new Date().getFullYear() + 1,
          new Date().getMonth(),
          1
        ),
      })],
    },
    {
      name: 'service_book',
      label: 'Service Book',
      type: 'select',
      options: [
        { value: 'available', label: 'Available' },
        { value: 'not_available', label: 'Not Available' },
        { value: 'partial', label: 'Partial' },
      ],
    },
    {
      name: 'cm3',
      label: 'Engine size (cm³)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'doors',
      label: 'Number of Doors',
      type: 'select',
      options: [
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
      ],
    },
    {
      name: 'co2',
      label: 'CO2 Emissions (g/km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'tank_capacity',
      label: 'Tank Capacity (L)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'fuel_consumption_2',
      label: 'Fuel Consumption (L/100km)',
      type: 'number',
      step: 0.1,
      validators: [
        Validators.min(0),
        Validators.max(50),
      ],
    },
    {
      name: 'cylinders',
      label: 'Number of Cylinders',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(16),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
  ],

  'crashed-cars': [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1990),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'electric', label: 'Electric' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'lpg', label: 'LPG' },
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      options: [
        { value: 'automatic', label: 'Automatic' },
        { value: 'manual', label: 'Manual' },
        { value: 'semi_automatic', label: 'Semi-automatic' },
      ],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (L)',
      type: 'number',
      step: 0.1,
      required: true,
      validators: [Validators.required, Validators.min(1), Validators.max(90)],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(7355),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'body_type',
      label: 'Body Type',
      type: 'select',
      options: [
        { value: 'sedan', label: 'Sedan' },
        { value: 'coupe', label: 'Coupé' },
        { value: 'convertible', label: 'Convertible' },
        { value: 'hatchback', label: 'Hatchback' },
        { value: 'station_wagon', label: 'Station Wagon' },
        { value: 'suv', label: 'SUV' },
        { value: 'pickup', label: 'Pickup' },
        { value: 'van', label: 'Van' },
        { value: 'minivan', label: 'Minivan' },
        { value: 'jeep', label: 'Jeep / Off-Road' },
        { value: 'limousine', label: 'Limousine' },
        { value: 'panel_van', label: 'Panel Van' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'damage_type',
      label: 'Damage Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'front', label: 'Front Damage' },
        { value: 'rear', label: 'Rear Damage' },
        { value: 'side', label: 'Side Damage' },
        { value: 'multiple', label: 'Multiple Areas' },
        { value: 'flood', label: 'Flood Damage' },
        { value: 'fire', label: 'Fire Damage' },
        { value: 'hail', label: 'Hail Damage' },
        { value: 'total_loss', label: 'Total Loss' },
      ],
    },
    {
      name: 'damage_severity',
      label: 'Damage Severity',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'light', label: 'Light Damage' },
        { value: 'medium', label: 'Medium Damage' },
        { value: 'heavy', label: 'Heavy Damage' },
        { value: 'total_loss', label: 'Total Loss' },
      ],
    },
    {
      name: 'repair_estimate',
      label: 'Repair Estimate (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0), Validators.max(200000)],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [dateFieldValidator({
        yearRange: { start: 1950, end: new Date().getFullYear() },
        maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      })],
    },
    {
      name: 'owners_count',
      label: 'Number of Previous Owners',
      type: 'select',
      options: [
        { value: 1, label: 'No Previous Owner' },
        { value: 2, label: 'First Owner' },
        { value: 3, label: 'Second Owner' },
        { value: 4, label: 'Third Owner' },
        { value: 5, label: 'Fourth Owner or More' },
      ],
      hint: 'Including yourself',
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'black', label: 'Black' },
        { value: 'white', label: 'White' },
        { value: 'grey', label: 'Grey' },
        { value: 'silver', label: 'Silver' },
        { value: 'blue', label: 'Blue' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'brown', label: 'Brown' },
        { value: 'beige', label: 'Beige' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'purple', label: 'Purple' },
        { value: 'gold', label: 'Gold' },
        { value: 'other', label: 'Other' },
      ],
    }
  ],
  'construction': [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
      placeholder: 'Enter brand',
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
      placeholder: 'Enter model',
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1960),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'operating_hours',
      label: 'Operating Hours',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'vehicle_type',
      label: 'Vehicle Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'excavator', label: 'Excavator' },
        { value: 'mini_excavator', label: 'Mini Excavator' },
        { value: 'midi_excavator', label: 'Midi Excavator' },
        { value: 'crawler_excavator', label: 'Crawler Excavator' },
        { value: 'wheeled_excavator', label: 'Wheeled Excavator' },
        { value: 'long_reach_excavator', label: 'Long Reach Excavator' },
        { value: 'bulldozer', label: 'Bulldozer' },
        { value: 'crawler_dozer', label: 'Crawler Dozer' },
        { value: 'wheel_dozer', label: 'Wheel Dozer' },
        { value: 'loader', label: 'Loader' },
        { value: 'wheel_loader', label: 'Wheel Loader' },
        { value: 'skid_steer_loader', label: 'Skid Steer Loader' },
        { value: 'backhoe_loader', label: 'Backhoe Loader' },
        { value: 'compact_track_loader', label: 'Compact Track Loader' },
        { value: 'grader', label: 'Grader' },
        { value: 'motor_grader', label: 'Motor Grader' },
        { value: 'roller', label: 'Roller' },
        { value: 'single_drum_roller', label: 'Single Drum Roller' },
        { value: 'tandem_roller', label: 'Tandem Roller' },
        { value: 'pneumatic_roller', label: 'Pneumatic Roller' },
        { value: 'compactor', label: 'Compactor' },
        { value: 'plate_compactor', label: 'Plate Compactor' },
        { value: 'vibratory_compactor', label: 'Vibratory Compactor' },
        { value: 'crane', label: 'Crane' },
        { value: 'mobile_crane', label: 'Mobile Crane' },
        { value: 'tower_crane', label: 'Tower Crane' },
        { value: 'crawler_crane', label: 'Crawler Crane' },
        { value: 'truck_crane', label: 'Truck Crane' },
        { value: 'forklift', label: 'Forklift' },
        { value: 'rough_terrain_forklift', label: 'Rough Terrain Forklift' },
        { value: 'telehandler', label: 'Telehandler' },
        { value: 'dump_truck', label: 'Dump Truck' },
        { value: 'articulated_dump_truck', label: 'Articulated Dump Truck' },
        { value: 'rigid_dump_truck', label: 'Rigid Dump Truck' },
        { value: 'paver', label: 'Paver' },
        { value: 'asphalt_paver', label: 'Asphalt Paver' },
        { value: 'concrete_paver', label: 'Concrete Paver' },
        { value: 'mixer', label: 'Mixer' },
        { value: 'concrete_mixer', label: 'Concrete Mixer' },
        { value: 'transit_mixer', label: 'Transit Mixer' },
        { value: 'piling_rig', label: 'Piling Rig' },
        { value: 'drilling_rig', label: 'Drilling Rig' },
        { value: 'crusher', label: 'Crusher' },
        { value: 'screener', label: 'Screener' },
        { value: 'mobile_crusher', label: 'Mobile Crusher' },
        { value: 'trencher', label: 'Trencher' },
        { value: 'boring_machine', label: 'Boring Machine' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'electric', label: 'Electric' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'dual_fuel', label: 'Dual Fuel' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (L)',
      type: 'number',
      step: 0.1,
      required: true,
      validators: [Validators.required, Validators.min(1), Validators.max(90)],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(2000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(3000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      options: [
        { value: 'manual', label: 'Manual' },
        { value: 'automatic', label: 'Automatic' },
        { value: 'powershift', label: 'Powershift' },
        { value: 'hydrostatic', label: 'Hydrostatic' },
        { value: 'mechanical', label: 'Mechanical' },
      ],
    },
    {
      name: 'drive_type',
      label: 'Drive Type',
      type: 'select',
      options: [
        { value: '2wd', label: '2WD' },
        { value: '4wd', label: '4WD' },
        { value: 'track', label: 'Track' },
        { value: 'articulated', label: 'Articulated' },
      ],
    },
    {
      name: 'operating_weight_kg',
      label: 'Operating Weight (kg)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(500),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'lifting_capacity_kg',
      label: 'Lifting Capacity (kg)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(100),
        Validators.max(1000000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bucket_capacity_m3',
      label: 'Bucket Capacity (m³)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0.1), Validators.max(50)],
    },
    {
      name: 'digging_depth_meters',
      label: 'Digging Depth (m)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0.1), Validators.max(50)],
    },
    {
      name: 'reach_meters',
      label: 'Reach (m)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0.1), Validators.max(100)],
    },
    {
      name: 'boom_length_meters',
      label: 'Boom Length (m)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0.5), Validators.max(200)],
    },
    {
      name: 'blade_width_meters',
      label: 'Blade Width (m)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0.5), Validators.max(20)],
    },
    {
      name: 'travel_speed_kmh',
      label: 'Travel Speed (km/h)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0), Validators.max(100)],
    },
    {
      name: 'cabin',
      label: 'Cabin Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'enclosed', label: 'Enclosed' },
        { value: 'open', label: 'Open' },
        { value: 'rops', label: 'ROPS (Roll Over Protection)' },
      ],
    },
    {
      name: 'air_conditioning',
      label: 'Air Conditioning',
      type: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      name: 'heating',
      label: 'Heating',
      type: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'refurbished', label: 'Refurbished' },
      ],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [
        dateFieldValidator({
          yearRange: { start: 1960, end: new Date().getFullYear() },
          maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        }),
      ],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
        { value: 'not_registered', label: 'Not Registered' },
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      options: [
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'blue', label: 'Blue' },
        { value: 'white', label: 'White' },
        { value: 'black', label: 'Black' },
        { value: 'grey', label: 'Grey' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'owners_count',
      label: 'Number of Owners',
      type: 'select',
      options: [
        { value: 1, label: 'No Previous Owner' },
        { value: 2, label: 'First Owner' },
        { value: 3, label: 'Second Owner' },
        { value: 4, label: 'Third Owner' },
        { value: 5, label: 'Fourth Owner' },
        { value: 6, label: 'Fifth Owner' },
        { value: 7, label: 'Sixth Owner' },
        { value: 8, label: 'Seventh Owner' },
        { value: 9, label: 'Eighth Owner' },
        { value: 10, label: 'Ninth Owner or More' },
      ],
    },
    {
      name: 'service_book',
      label: 'Service Book',
      type: 'select',
      options: [
        { value: 'available', label: 'Available' },
        { value: 'not_available', label: 'Not Available' },
        { value: 'partial', label: 'Partial' },
      ],
    },
    {
      name: 'hydraulic_system',
      label: 'Hydraulic System',
      type: 'select',
      options: [
        { value: 'standard', label: 'Standard' },
        { value: 'high_flow', label: 'High Flow' },
        { value: 'dual_circuit', label: 'Dual Circuit' },
      ],
    },
    {
      name: 'quick_coupler',
      label: 'Quick Coupler',
      type: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      name: 'euro_emission_class',
      label: 'Euro Emission Class',
      type: 'select',
      options: [
        { value: 1, label: 'Euro 1' },
        { value: 2, label: 'Euro 2' },
        { value: 3, label: 'Euro 3' },
        { value: 4, label: 'Euro 4' },
        { value: 5, label: 'Euro 5' },
        { value: 6, label: 'Euro 6' },
      ],
    },
    {
      name: 'undercarriage_condition',
      label: 'Undercarriage Condition',
      type: 'select',
      options: [
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'track_width_mm',
      label: 'Track Width (mm)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(100),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
  ],
  'agriculture': [
    ...baseFields,
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
      placeholder: 'Enter brand',
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(100)],
      placeholder: 'Enter model',
    },
    {
      name: 'year',
      label: 'Year',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1950),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    ...basePriceFields,
    {
      name: 'operating_hours',
      label: 'Operating Hours',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(0),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'mileage',
      label: 'Mileage (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(500000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'vehicle_type',
      label: 'Vehicle Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'tractor', label: 'Tractor' },
        { value: 'compact_tractor', label: 'Compact Tractor' },
        { value: 'utility_tractor', label: 'Utility Tractor' },
        { value: 'row_crop_tractor', label: 'Row Crop Tractor' },
        { value: 'orchard_tractor', label: 'Orchard Tractor' },
        { value: 'combine_harvester', label: 'Combine Harvester' },
        { value: 'cotton_picker', label: 'Cotton Picker' },
        { value: 'forage_harvester', label: 'Forage Harvester' },
        { value: 'baler', label: 'Baler' },
        { value: 'round_baler', label: 'Round Baler' },
        { value: 'square_baler', label: 'Square Baler' },
        { value: 'planter', label: 'Planter' },
        { value: 'seeder', label: 'Seeder' },
        { value: 'grain_drill', label: 'Grain Drill' },
        { value: 'sprayer', label: 'Sprayer' },
        { value: 'self_propelled_sprayer', label: 'Self-Propelled Sprayer' },
        { value: 'trailed_sprayer', label: 'Trailed Sprayer' },
        { value: 'cultivator', label: 'Cultivator' },
        { value: 'plow', label: 'Plow' },
        { value: 'disc_harrow', label: 'Disc Harrow' },
        { value: 'loader', label: 'Loader' },
        { value: 'telehandler', label: 'Telehandler' },
        { value: 'skid_steer', label: 'Skid Steer' },
        { value: 'mower', label: 'Mower' },
        { value: 'rotary_mower', label: 'Rotary Mower' },
        { value: 'flail_mower', label: 'Flail Mower' },
        { value: 'trailer', label: 'Trailer' },
        { value: 'grain_trailer', label: 'Grain Trailer' },
        { value: 'silage_trailer', label: 'Silage Trailer' },
        { value: 'spreader', label: 'Spreader' },
        { value: 'manure_spreader', label: 'Manure Spreader' },
        { value: 'fertilizer_spreader', label: 'Fertilizer Spreader' },
        { value: 'irrigation', label: 'Irrigation' },
        { value: 'irrigation_system', label: 'Irrigation System' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'fuel',
      label: 'Fuel Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'diesel', label: 'Diesel' },
        { value: 'petrol', label: 'Petrol' },
        { value: 'electric', label: 'Electric' },
        { value: 'hybrid', label: 'Hybrid' },
        { value: 'biodiesel', label: 'Biodiesel' },
        { value: 'dual_fuel', label: 'Dual Fuel' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'engine_size',
      label: 'Engine Size (L)',
      type: 'number',
      step: 0.1,
      required: true,
      validators: [Validators.required, Validators.min(0.5), Validators.max(90)],
    },
    {
      name: 'power_kw',
      label: 'Power (kW)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'power_hp',
      label: 'Power (HP)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(1500),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'transmission',
      label: 'Transmission',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'manual', label: 'Manual' },
        { value: 'automatic', label: 'Automatic' },
        { value: 'powershift', label: 'Powershift' },
        { value: 'cvt', label: 'CVT (Continuously Variable)' },
        { value: 'hydrostatic', label: 'Hydrostatic' },
        { value: 'mechanical', label: 'Mechanical' },
      ],
    },
    {
      name: 'drive_type',
      label: 'Drive Type',
      type: 'select',
      options: [
        { value: '2wd', label: '2WD' },
        { value: '4wd', label: '4WD' },
        { value: 'mfwd', label: 'MFWD (Mechanical Front-Wheel Drive)' },
        { value: 'articulated', label: 'Articulated' },
      ],
    },
    {
      name: 'pto',
      label: 'PTO (Power Take-Off)',
      type: 'select',
      options: [
        { value: '540_rpm', label: '540 RPM' },
        { value: '1000_rpm', label: '1000 RPM' },
        { value: '540_1000_rpm', label: '540/1000 RPM' },
        { value: 'not_available', label: 'Not Available' },
      ],
    },
    {
      name: 'hydraulic_outlets',
      label: 'Hydraulic Outlets',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(12),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'working_width_meters',
      label: 'Working Width (m)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0.1), Validators.max(50)],
    },
    {
      name: 'operating_weight_kg',
      label: 'Operating Weight (kg)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(100),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'cabin',
      label: 'Cabin Type',
      type: 'select',
      options: [
        { value: 'enclosed', label: 'Enclosed' },
        { value: 'open', label: 'Open' },
        { value: 'rops', label: 'ROPS (Roll Over Protection)' },
        { value: 'rops_with_canopy', label: 'ROPS with Canopy' },
      ],
    },
    {
      name: 'air_conditioning',
      label: 'Air Conditioning',
      type: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'used', label: 'Used' },
        { value: 'refurbished', label: 'Refurbished' },
      ],
    },
    {
      name: 'first_registration',
      label: 'First Registration',
      type: 'first_registration_dropdown',
      validators: [
        dateFieldValidator({
          yearRange: { start: 1950, end: new Date().getFullYear() },
          maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        }),
      ],
    },
    {
      name: 'registration',
      label: 'Registration',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'macedonian', label: 'Macedonian' },
        { value: 'other', label: 'Other' },
        { value: 'not_registered', label: 'Not Registered' },
      ],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'select',
      options: [
        { value: 'red', label: 'Red' },
        { value: 'green', label: 'Green' },
        { value: 'blue', label: 'Blue' },
        { value: 'yellow', label: 'Yellow' },
        { value: 'orange', label: 'Orange' },
        { value: 'white', label: 'White' },
        { value: 'black', label: 'Black' },
        { value: 'grey', label: 'Grey' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'owners_count',
      label: 'Number of Owners',
      type: 'select',
      options: [
        { value: 1, label: 'No Previous Owner' },
        { value: 2, label: 'First Owner' },
        { value: 3, label: 'Second Owner' },
        { value: 4, label: 'Third Owner' },
        { value: 5, label: 'Fourth Owner' },
        { value: 6, label: 'Fifth Owner' },
        { value: 7, label: 'Sixth Owner' },
        { value: 8, label: 'Seventh Owner' },
        { value: 9, label: 'Eighth Owner' },
        { value: 10, label: 'Ninth Owner or More' },
      ],
    },
    {
      name: 'service_book',
      label: 'Service Book',
      type: 'select',
      options: [
        { value: 'available', label: 'Available' },
        { value: 'not_available', label: 'Not Available' },
        { value: 'partial', label: 'Partial' },
      ],
    },
    {
      name: 'tyres',
      label: 'Tyre Type',
      type: 'select',
      options: [
        { value: 'agricultural', label: 'Agricultural' },
        { value: 'turf', label: 'Turf' },
        { value: 'industrial', label: 'Industrial' },
        { value: 'flotation', label: 'Flotation' },
      ],
    },
    {
      name: 'front_tyre_size',
      label: 'Front Tyre Size',
      type: 'text',
      validators: [Validators.maxLength(50)],
      placeholder: 'e.g., 380/85R24',
    },
    {
      name: 'rear_tyre_size',
      label: 'Rear Tyre Size',
      type: 'text',
      validators: [Validators.maxLength(50)],
      placeholder: 'e.g., 480/80R46',
    },
    {
      name: 'gps_system',
      label: 'GPS System',
      type: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
    {
      name: 'auto_steer',
      label: 'Auto Steer',
      type: 'select',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    },
  ],
};

export const realEstateFormConfigs: CategoryFormConfig = {
  'house-sale': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'property_type',
      label: 'Property Type',
      type: 'select',
      options: [
        { value: 'detached', label: 'Detached House' },
        { value: 'semi_detached', label: 'Semi-detached House' },
        { value: 'townhouse', label: 'Townhouse' },
        { value: 'villa', label: 'Villa' },
        { value: 'cottage', label: 'Cottage' },
        { value: 'mansion', label: 'Mansion' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Living Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'lot_size',
      label: 'Lot Size (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'rooms',
      label: 'Total Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bedrooms',
      label: 'Bedrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bathrooms',
      label: 'Bathrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'floors',
      label: 'Number of Floors',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'construction_year',
      label: 'Year Built',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'renovation_year',
      label: 'Year Renovated',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'needs_renovation', label: 'Needs Renovation' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'heating',
      label: 'Heating',
      type: 'select',
      options: [
        { value: 'central', label: 'Central Heating' },
        { value: 'gas', label: 'Gas' },
        { value: 'electric', label: 'Electric' },
        { value: 'oil', label: 'Oil' },
        { value: 'wood', label: 'Wood' },
        { value: 'solar', label: 'Solar' },
        { value: 'heat_pump', label: 'Heat Pump' },
        { value: 'none', label: 'None' },
      ],
    },
    {
      name: 'energy_class',
      label: 'Energy Class',
      type: 'select',
      options: [
        { value: 'A+', label: 'A+' },
        { value: 'A', label: 'A' },
        { value: 'B', label: 'B' },
        { value: 'C', label: 'C' },
        { value: 'D', label: 'D' },
        { value: 'E', label: 'E' },
        { value: 'F', label: 'F' },
        { value: 'G', label: 'G' },
      ],
    },
    {
      name: 'garage',
      label: 'Garage',
      type: 'checkbox',
    },
    {
      name: 'garden',
      label: 'Garden',
      type: 'checkbox',
    },
    {
      name: 'basement',
      label: 'Basement',
      type: 'checkbox',
    },
    {
      name: 'balcony',
      label: 'Balcony',
      type: 'checkbox',
    },
    {
      name: 'terrace',
      label: 'Terrace',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'additional_costs',
      label: 'Additional Costs (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0), Validators.max(1000)],
    },
    {
      name: 'commission',
      label: 'Commission (%)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
  ],

  'house-rent': [
    ...baseFields,
    {
      name: 'monthly_rent',
      label: 'Monthly Rent (€)',
      type: 'number',
      required: true,
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000)],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'deposit',
      label: 'Security Deposit (€)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',

      validators: [
        Validators.min(1),
        Validators.max(1000),

        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'utilities',
      label: 'Utilities (€/month)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',

      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'utilities_included',
      label: 'Utilities Included',
      type: 'checkbox',
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',

      validators: [
        Validators.min(1),
        Validators.max(120),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'available_from',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'property_type',
      label: 'Property Type',
      type: 'select',
      options: [
        { value: 'detached', label: 'Detached House' },
        { value: 'semi_detached', label: 'Semi-detached House' },
        { value: 'townhouse', label: 'Townhouse' },
        { value: 'villa', label: 'Villa' },
        { value: 'cottage', label: 'Cottage' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Living Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'rooms',
      label: 'Total Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bedrooms',
      label: 'Bedrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bathrooms',
      label: 'Bathrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'floors',
      label: 'Number of Floors',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'construction_year',
      label: 'Year Built',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'heating',
      label: 'Heating',
      type: 'select',
      options: [
        { value: 'central', label: 'Central Heating' },
        { value: 'gas', label: 'Gas' },
        { value: 'electric', label: 'Electric' },
        { value: 'oil', label: 'Oil' },
        { value: 'wood', label: 'Wood' },
        { value: 'solar', label: 'Solar' },
        { value: 'heat_pump', label: 'Heat Pump' },
        { value: 'included', label: 'Included' },
      ],
    },
    {
      name: 'energy_class',
      label: 'Energy Class',
      type: 'select',
      options: [
        { value: 'A+', label: 'A+' },
        { value: 'A', label: 'A' },
        { value: 'B', label: 'B' },
        { value: 'C', label: 'C' },
        { value: 'D', label: 'D' },
        { value: 'E', label: 'E' },
        { value: 'F', label: 'F' },
        { value: 'G', label: 'G' },
      ],
    },
    {
      name: 'furnished',
      label: 'Furnished',
      type: 'select',
      options: [
        { value: 'furnished', label: 'Furnished' },
        { value: 'semi_furnished', label: 'Semi-furnished' },
        { value: 'unfurnished', label: 'Unfurnished' },
      ],
    },
    {
      name: 'garage',
      label: 'Garage',
      type: 'checkbox',
    },
    {
      name: 'garden',
      label: 'Garden',
      type: 'checkbox',
    },
    {
      name: 'basement',
      label: 'Basement',
      type: 'checkbox',
    },
    {
      name: 'balcony',
      label: 'Balcony',
      type: 'checkbox',
    },
    {
      name: 'terrace',
      label: 'Terrace',
      type: 'checkbox',
    },
    {
      name: 'pets_allowed',
      label: 'Pets Allowed',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'apartments-sale': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'apartment_type',
      label: 'Apartment Type',
      type: 'select',
      options: [
        { value: 'studio', label: 'Studio' },
        { value: 'loft', label: 'Loft' },
        { value: 'penthouse', label: 'Penthouse' },
        { value: 'duplex', label: 'Duplex' },
        { value: 'standard', label: 'Standard' },
        { value: 'maisonette', label: 'Maisonette' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Living Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'rooms',
      label: 'Total Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bedrooms',
      label: 'Bedrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bathrooms',
      label: 'Bathrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'floor',
      label: 'Floor',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(-5),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'total_floors',
      label: 'Total Floors in Building',
      type: 'number',
      validators: [Validators.min(-5), Validators.max(50)],
    },
    {
      name: 'construction_year',
      label: 'Year Built',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'renovation_year',
      label: 'Year Renovated',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'needs_renovation', label: 'Needs Renovation' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'heating',
      label: 'Heating',
      type: 'select',
      options: [
        { value: 'central', label: 'Central Heating' },
        { value: 'gas', label: 'Gas' },
        { value: 'electric', label: 'Electric' },
        { value: 'district', label: 'District Heating' },
        { value: 'heat_pump', label: 'Heat Pump' },
      ],
    },
    {
      name: 'energy_class',
      label: 'Energy Class',
      type: 'select',
      options: [
        { value: 'A+', label: 'A+' },
        { value: 'A', label: 'A' },
        { value: 'B', label: 'B' },
        { value: 'C', label: 'C' },
        { value: 'D', label: 'D' },
        { value: 'E', label: 'E' },
        { value: 'F', label: 'F' },
        { value: 'G', label: 'G' },
      ],
    },
    {
      name: 'balcony',
      label: 'Balcony',
      type: 'checkbox',
    },
    {
      name: 'terrace',
      label: 'Terrace',
      type: 'checkbox',
    },
    {
      name: 'elevator',
      label: 'Elevator',
      type: 'checkbox',
    },
    {
      name: 'parking',
      label: 'Parking',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'monthly_costs',
      label: 'Monthly Costs (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000000)],
    },
    {
      name: 'additional_costs',
      label: 'Additional Costs (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0), Validators.max(1000)],
    },
    {
      name: 'commission',
      label: 'Commission (%)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
  ],

  'apartments-rent': [
    ...baseFields,
    {
      name: 'monthly_rent',
      label: 'Monthly Rent (€)',
      type: 'number',
      required: true,
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000000)],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'deposit',
      label: 'Security Deposit (€)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'utilities',
      label: 'Utilities (€/month)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'utilities_included',
      label: 'Utilities Included',
      type: 'checkbox',
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(120),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'available_from',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'apartment_type',
      label: 'Apartment Type',
      type: 'select',
      options: [
        { value: 'studio', label: 'Studio' },
        { value: 'loft', label: 'Loft' },
        { value: 'penthouse', label: 'Penthouse' },
        { value: 'duplex', label: 'Duplex' },
        { value: 'standard', label: 'Standard' },
        { value: 'maisonette', label: 'Maisonette' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Living Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'rooms',
      label: 'Total Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bedrooms',
      label: 'Bedrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bathrooms',
      label: 'Bathrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(15),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'floor',
      label: 'Floor',
      type: 'number',
      step: 1,
      pattern: '^(-[1-5]|[0-9])$',
      validators: [
        Validators.min(-5),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^(-?[0-5]|[0-9])$/),
      ],
    },
    {
      name: 'total_floors',
      label: 'Total Floors in Building',
      type: 'number',
      step: 1,
      pattern: '^(-[1-5]|[0-9])$',
      validators: [
        Validators.min(-5),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^(-?[0-5]|[0-9])$/),
      ],
    },
    {
      name: 'construction_year',
      label: 'Year Built',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'heating',
      label: 'Heating',
      type: 'select',
      options: [
        { value: 'central', label: 'Central Heating' },
        { value: 'gas', label: 'Gas' },
        { value: 'electric', label: 'Electric' },
        { value: 'district', label: 'District Heating' },
        { value: 'heat_pump', label: 'Heat Pump' },
        { value: 'included', label: 'Included' },
      ],
    },
    {
      name: 'energy_class',
      label: 'Energy Class',
      type: 'select',
      options: [
        { value: 'A+', label: 'A+' },
        { value: 'A', label: 'A' },
        { value: 'B', label: 'B' },
        { value: 'C', label: 'C' },
        { value: 'D', label: 'D' },
        { value: 'E', label: 'E' },
        { value: 'F', label: 'F' },
        { value: 'G', label: 'G' },
      ],
    },
    {
      name: 'furnished',
      label: 'Furnished',
      type: 'select',
      options: [
        { value: 'furnished', label: 'Furnished' },
        { value: 'semi_furnished', label: 'Semi-furnished' },
        { value: 'unfurnished', label: 'Unfurnished' },
      ],
    },
    {
      name: 'balcony',
      label: 'Balcony',
      type: 'checkbox',
    },
    {
      name: 'terrace',
      label: 'Terrace',
      type: 'checkbox',
    },
    {
      name: 'elevator',
      label: 'Elevator',
      type: 'checkbox',
    },
    {
      name: 'parking',
      label: 'Parking',
      type: 'checkbox',
    },
    {
      name: 'pets_allowed',
      label: 'Pets Allowed',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'land-sale': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'land_type',
      label: 'Land Type',
      type: 'select',
      options: [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial', label: 'Commercial' },
        { value: 'industrial', label: 'Industrial' },
        { value: 'agricultural', label: 'Agricultural' },
        { value: 'forest', label: 'Forest' },
        { value: 'recreational', label: 'Recreational' },
        { value: 'mixed', label: 'Mixed' },
      ],
    },
    {
      name: 'total_area',
      label: 'Total Area (m²)',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'buildable_area',
      label: 'Buildable Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'building_rights',
      label: 'Building Rights',
      type: 'checkbox',
    },
    {
      name: 'max_building_height',
      label: 'Max Building Height (m)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(800),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'utilities_available',
      label: 'Utilities Available',
      type: 'checkbox',
    },
    {
      name: 'road_access',
      label: 'Road Access',
      type: 'checkbox',
    },
    {
      name: 'development_plan',
      label: 'Development Plan',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'zoning',
      label: 'Zoning',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'development_costs',
      label: 'Development Costs (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(1000000)],
    },
    {
      name: 'annual_taxes',
      label: 'Annual Taxes (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(1000000)],
    },
  ],

  'land-rent': [
    ...baseFields,
    {
      name: 'monthly_rent',
      label: 'Monthly Rent (€)',
      type: 'number',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(100000.0),
      ],
      step: 0.01,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      validators: [Validators.min(1), Validators.max(120)],
    },
    {
      name: 'land_type',
      label: 'Land Type',
      type: 'select',
      options: [
        { value: 'agricultural', label: 'Agricultural' },
        { value: 'recreational', label: 'Recreational' },
        { value: 'storage', label: 'Storage' },
        { value: 'parking', label: 'Parking' },
        { value: 'garden', label: 'Garden' },
        { value: 'camping', label: 'Camping' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'total_area',
      label: 'Total Area (m²)',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(100000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'usage_permissions',
      label: 'Usage Permissions',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'available_from',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'business-office-sale': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'office_type',
      label: 'Office Type',
      type: 'select',
      options: [
        { value: 'single_office', label: 'Single Office' },
        { value: 'open_space', label: 'Open Space' },
        { value: 'co_working', label: 'Co-working' },
        { value: 'executive_suite', label: 'Executive Suite' },
        { value: 'warehouse', label: 'Warehouse' },
        { value: 'retail_space', label: 'Retail Space' },
        { value: 'restaurant', label: 'Restaurant' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Floor Area (m²)',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'rooms',
      label: 'Number of Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'floor',
      label: 'Floor',
      type: 'number',
      step: 1,
      pattern: '^(-[1-5]|[0-9])$',
      validators: [
        Validators.min(-5),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^(-?[0-5]|[0-9])$/),
      ],
    },
    {
      name: 'total_floors',
      label: 'Total Floors in Building',
      type: 'number',
      step: 1,
      pattern: '^(-[1-5]|[0-9])$',
      validators: [
        Validators.min(-5),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^(-?[0-5]|[0-9])$/),
      ],
    },
    {
      name: 'construction_year',
      label: 'Year Built',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'needs_renovation', label: 'Needs Renovation' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'heating',
      label: 'Heating/Cooling',
      type: 'select',
      options: [
        { value: 'central', label: 'Central Heating' },
        { value: 'gas', label: 'Gas' },
        { value: 'electric', label: 'Electric' },
        { value: 'district', label: 'District Heating' },
        { value: 'air_conditioning', label: 'Air Conditioning' },
      ],
    },
    {
      name: 'parking_spaces',
      label: 'Parking Spaces',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(500),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'elevator',
      label: 'Elevator',
      type: 'checkbox',
    },
    {
      name: 'reception',
      label: 'Reception Area',
      type: 'checkbox',
    },
    {
      name: 'conference_room',
      label: 'Conference Room',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'current_rental_income',
      label: 'Current Rental Income (€/month)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000)],
    },
    {
      name: 'operating_costs',
      label: 'Operating Costs (€/month)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000)],
    },
  ],

  'business-office-rent': [
    ...baseFields,
    {
      name: 'monthly_rent',
      label: 'Monthly Rent (€)',
      type: 'number',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(100000.0),
      ],
      step: 0.01,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'deposit',
      label: 'Security Deposit (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(1000)],
    },
    {
      name: 'utilities',
      label: 'Utilities (€/month)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(1000)],
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      validators: [Validators.min(1), Validators.max(120)],
    },
    {
      name: 'available_from',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'office_type',
      label: 'Office Type',
      type: 'select',
      options: [
        { value: 'single_office', label: 'Single Office' },
        { value: 'open_space', label: 'Open Space' },
        { value: 'co_working', label: 'Co-working' },
        { value: 'executive_suite', label: 'Executive Suite' },
        { value: 'warehouse', label: 'Warehouse' },
        { value: 'retail_space', label: 'Retail Space' },
        { value: 'restaurant', label: 'Restaurant' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Floor Area (m²)',
      type: 'number',
      required: true,
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),

        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'rooms',
      label: 'Number of Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'floor',
      label: 'Floor',
      type: 'number',
      step: 1,
      pattern: '^(-[1-5]|[0-9])$',
      validators: [
        Validators.min(-5),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^(-?[0-5]|[0-9])$/),
      ],
    },
    {
      name: 'parking_spaces',
      label: 'Parking Spaces',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(500),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'elevator',
      label: 'Elevator',
      type: 'checkbox',
    },
    {
      name: 'reception',
      label: 'Reception Area',
      type: 'checkbox',
    },
    {
      name: 'conference_room',
      label: 'Conference Room',
      type: 'checkbox',
    },
    {
      name: 'furnished',
      label: 'Furnished',
      type: 'select',
      options: [
        { value: 'furnished', label: 'Furnished' },
        { value: 'semi_furnished', label: 'Semi-furnished' },
        { value: 'unfurnished', label: 'Unfurnished' },
      ],
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'garage-sale': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'garage_type',
      label: 'Garage Type',
      type: 'select',
      options: [
        { value: 'single_garage', label: 'Single Garage' },
        { value: 'double_garage', label: 'Double Garage' },
        { value: 'parking_space', label: 'Parking Space' },
        { value: 'underground', label: 'Underground' },
        { value: 'carport', label: 'Carport' },
        { value: 'storage_unit', label: 'Storage Unit' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'height_meters',
      label: 'Height (m)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'electricity',
      label: 'Electricity',
      type: 'checkbox',
    },
    {
      name: 'automatic_door',
      label: 'Automatic Door',
      type: 'checkbox',
    },
    {
      name: 'security_system',
      label: 'Security System',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'garage-rent': [
    ...baseFields,
    {
      name: 'monthly_rent',
      label: 'Monthly Rent (€)',
      type: 'number',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(1000.0),
      ],
      step: 0.01,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'deposit',
      label: 'Security Deposit (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(1000)],
    },
    {
      name: 'available_from',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(120),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'garage_type',
      label: 'Garage Type',
      type: 'select',
      options: [
        { value: 'single_garage', label: 'Single Garage' },
        { value: 'double_garage', label: 'Double Garage' },
        { value: 'parking_space', label: 'Parking Space' },
        { value: 'underground', label: 'Underground' },
        { value: 'carport', label: 'Carport' },
        { value: 'storage_unit', label: 'Storage Unit' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'electricity',
      label: 'Electricity',
      type: 'checkbox',
    },
    {
      name: 'automatic_door',
      label: 'Automatic Door',
      type: 'checkbox',
    },
    {
      name: 'security_system',
      label: 'Security System',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'luxury-real-estate-sale': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'property_type',
      label: 'Property Type',
      type: 'select',
      options: [
        { value: 'villa', label: 'Villa' },
        { value: 'mansion', label: 'Mansion' },
        { value: 'penthouse', label: 'Penthouse' },
        { value: 'castle', label: 'Castle' },
        { value: 'estate', label: 'Estate' },
        { value: 'yacht', label: 'Yacht' },
        { value: 'private_island', label: 'Private Island' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Living Area (m²)',
      type: 'number',
      validators: [Validators.min(1), Validators.max(10000)],
    },
    {
      name: 'lot_size',
      label: 'Lot Size (m²)',
      type: 'number',
      validators: [Validators.min(1), Validators.max(1000000)],
    },
    {
      name: 'rooms',
      label: 'Total Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bedrooms',
      label: 'Bedrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bathrooms',
      label: 'Bathrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'floors',
      label: 'Number of Floors',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(20),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'construction_year',
      label: 'Year Built',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'architect',
      label: 'Architect',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'renovation_year',
      label: 'Year Renovated',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1900),
        Validators.max(new Date().getFullYear() + 1),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'new', label: 'New' },
        { value: 'excellent', label: 'Excellent' },
        { value: 'good', label: 'Good' },
        { value: 'historic', label: 'Historic' },
      ],
    },
    {
      name: 'swimming_pool',
      label: 'Swimming Pool',
      type: 'checkbox',
    },
    {
      name: 'tennis_court',
      label: 'Tennis Court',
      type: 'checkbox',
    },
    {
      name: 'wine_cellar',
      label: 'Wine Cellar',
      type: 'checkbox',
    },
    {
      name: 'home_theater',
      label: 'Home Theater',
      type: 'checkbox',
    },
    {
      name: 'gym',
      label: 'Gym',
      type: 'checkbox',
    },
    {
      name: 'spa',
      label: 'Spa',
      type: 'checkbox',
    },
    {
      name: 'guest_house',
      label: 'Guest House',
      type: 'checkbox',
    },
    {
      name: 'staff_quarters',
      label: 'Staff Quarters',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'luxury-real-estate-rent': [
    ...baseFields,
    {
      name: 'monthly_rent',
      label: 'Monthly Rent (€)',
      type: 'number',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(1000000.0),
      ],
      step: 0.01,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'deposit',
      label: 'Security Deposit (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(10000)],
    },
    {
      name: 'utilities_included',
      label: 'Utilities Included',
      type: 'checkbox',
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(120),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'available_from',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'property_type',
      label: 'Property Type',
      type: 'select',
      options: [
        { value: 'villa', label: 'Villa' },
        { value: 'mansion', label: 'Mansion' },
        { value: 'penthouse', label: 'Penthouse' },
        { value: 'luxury_apartment', label: 'Luxury Apartment' },
        { value: 'estate', label: 'Estate' },
        { value: 'yacht', label: 'Yacht' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Living Area (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(10000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'rooms',
      label: 'Total Rooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bedrooms',
      label: 'Bedrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'bathrooms',
      label: 'Bathrooms',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'swimming_pool',
      label: 'Swimming Pool',
      type: 'checkbox',
    },
    {
      name: 'tennis_court',
      label: 'Tennis Court',
      type: 'checkbox',
    },
    {
      name: 'wine_cellar',
      label: 'Wine Cellar',
      type: 'checkbox',
    },
    {
      name: 'home_theater',
      label: 'Home Theater',
      type: 'checkbox',
    },
    {
      name: 'gym',
      label: 'Gym',
      type: 'checkbox',
    },
    {
      name: 'spa',
      label: 'Spa',
      type: 'checkbox',
    },
    {
      name: 'concierge_service',
      label: 'Concierge Service',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'room-rent': [
    ...baseFields,
    {
      name: 'monthly_rent',
      label: 'Monthly Rent (€)',
      type: 'number',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000.0),
      ],
      step: 0.01,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'deposit',
      label: 'Security Deposit (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(10000)],
    },
    {
      name: 'utilities',
      label: 'Utilities (€/month)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(10000)],
    },
    {
      name: 'utilities_included',
      label: 'Utilities Included',
      type: 'checkbox',
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(120),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'available_from',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'room_type',
      label: 'Room Type',
      type: 'select',
      options: [
        { value: 'single_room', label: 'Single Room' },
        { value: 'shared_room', label: 'Shared Room' },
        { value: 'master_bedroom', label: 'Master Bedroom' },
        { value: 'studio_room', label: 'Studio Room' },
      ],
    },
    {
      name: 'square_meters',
      label: 'Room Size (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'furnished',
      label: 'Furnished',
      type: 'checkbox',
    },
    {
      name: 'private_bathroom',
      label: 'Private Bathroom',
      type: 'checkbox',
    },
    {
      name: 'shared_kitchen',
      label: 'Shared Kitchen',
      type: 'checkbox',
    },
    {
      name: 'shared_living_room',
      label: 'Shared Living Room',
      type: 'checkbox',
    },
    {
      name: 'shared_bathroom',
      label: 'Shared Bathroom',
      type: 'checkbox',
    },
    {
      name: 'total_roommates',
      label: 'Total Roommates',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(20),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'house_type',
      label: 'House Type',
      type: 'select',
      options: [
        { value: 'apartment', label: 'Apartment' },
        { value: 'house', label: 'House' },
        { value: 'wg', label: 'WG' },
        { value: 'student_dorm', label: 'Student Dorm' },
      ],
    },
    {
      name: 'gender_preference',
      label: 'Gender Preference',
      type: 'select',
      options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'any', label: 'Any' },
      ],
    },
    {
      name: 'age_preference_min',
      label: 'Minimum Age Preference',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(18),
        Validators.max(10),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'age_preference_max',
      label: 'Maximum Age Preference',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(18),
        Validators.max(10),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'students_welcome',
      label: 'Students Welcome',
      type: 'checkbox',
    },
    {
      name: 'smoking_allowed',
      label: 'Smoking Allowed',
      type: 'checkbox',
    },
    {
      name: 'pets_allowed',
      label: 'Pets Allowed',
      type: 'checkbox',
    },
    {
      name: 'address',
      label: 'Street Address',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'postal_code',
      label: 'Postal Code',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'country',
      label: 'Country',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
  ],

  'looking-for-roommate': [
    ...baseFields,
    {
      name: 'budget_max',
      label: 'Maximum Budget (€)',
      type: 'number',
      required: true,
      validators: [
        Validators.required,
        Validators.min(1),
        Validators.max(10000.0),
      ],
      step: 0.01,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'room_type',
      label: 'Room Type Wanted',
      type: 'select',
      options: [
        { value: 'single_room', label: 'Single Room' },
        { value: 'shared_room', label: 'Shared Room' },
        { value: 'studio', label: 'Studio' },
        { value: 'apartment_share', label: 'Apartment Share' },
      ],
    },
    {
      name: 'min_square_meters',
      label: 'Minimum Size (m²)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'preferred_furnished',
      label: 'Prefer Furnished',
      type: 'checkbox',
    },
    {
      name: 'private_bathroom_needed',
      label: 'Private Bathroom Needed',
      type: 'checkbox',
    },
    {
      name: 'age',
      label: 'Your Age',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(18),
        Validators.max(60),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'gender',
      label: 'Your Gender',
      type: 'select',
      options: [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'occupation',
      label: 'Occupation',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'smoking',
      label: 'I Smoke',
      type: 'checkbox',
    },
    {
      name: 'has_pets',
      label: 'I Have Pets',
      type: 'checkbox',
    },
    {
      name: 'preferred_location',
      label: 'Preferred Location',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'move_in_date',
      label: 'Move-in Date',
      type: 'date',
    },
    {
      name: 'min_lease_months',
      label: 'Minimum Lease (months)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(120),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'max_roommates',
      label: 'Maximum Roommates',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(20),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
  ],
};

export const jobsFormConfigs: CategoryFormConfig = {
  'home-maintenance': [
    ...baseFields,
    {
      name: 'pricing_type',
      label: 'Pricing Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'hourly', label: 'Hourly Rate' },
        { value: 'daily', label: 'Daily Rate' },
        { value: 'project', label: 'Project-based' },
        { value: 'monthly', label: 'Monthly Rate' },
        { value: 'per_session', label: 'Per Session' },
        { value: 'negotiable', label: 'Negotiable' },
      ],
    },
    {
      name: 'price_min',
      label: 'Minimum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'price_max',
      label: 'Maximum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'remote_available',
      label: 'Remote Service Available',
      type: 'checkbox',
    },
    {
      name: 'travel_willing',
      label: 'Willing to Travel',
      type: 'checkbox',
    },
    {
      name: 'max_travel_distance',
      label: 'Max Travel Distance (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'availability',
      label: 'Availability',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'start_availability',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'service_type',
      label: 'Service Type',
      type: 'select',
      options: [
        { value: 'cleaning', label: 'Cleaning' },
        { value: 'plumbing', label: 'Plumbing' },
        { value: 'electrical', label: 'Electrical' },
        { value: 'painting', label: 'Painting' },
        { value: 'carpentry', label: 'Carpentry' },
        { value: 'gardening', label: 'Gardening' },
        { value: 'handyman', label: 'Handyman' },
        { value: 'moving', label: 'Moving' },
        { value: 'renovation', label: 'Renovation' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'service_category',
      label: 'Service Category',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'min_job_size',
      label: 'Minimum Job Size',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'max_job_size',
      label: 'Maximum Job Size',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'emergency_service',
      label: 'Emergency Service Available',
      type: 'checkbox',
    },
    {
      name: 'weekend_available',
      label: 'Weekend Availability',
      type: 'checkbox',
    },
    {
      name: 'experience_years',
      label: 'Years of Experience',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'own_tools',
      label: 'Bring Own Tools',
      type: 'checkbox',
    },
    {
      name: 'own_materials',
      label: 'Provide Materials',
      type: 'checkbox',
    },
    {
      name: 'licensed_insured',
      label: 'Licensed & Insured',
      type: 'checkbox',
    },
    {
      name: 'service_radius',
      label: 'Service Radius (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(200),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'travel_cost_per_km',
      label: 'Travel Cost per km (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0), Validators.max(100.0)],
    },
  ],

  'personal-care-wellness': [
    ...baseFields,
    {
      name: 'pricing_type',
      label: 'Pricing Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'hourly', label: 'Hourly Rate' },
        { value: 'daily', label: 'Daily Rate' },
        { value: 'project', label: 'Project-based' },
        { value: 'monthly', label: 'Monthly Rate' },
        { value: 'per_session', label: 'Per Session' },
        { value: 'negotiable', label: 'Negotiable' },
      ],
    },
    {
      name: 'price_min',
      label: 'Minimum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'price_max',
      label: 'Maximum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'remote_available',
      label: 'Remote Sessions Available',
      type: 'checkbox',
    },
    {
      name: 'travel_willing',
      label: 'Willing to Travel',
      type: 'checkbox',
    },
    {
      name: 'max_travel_distance',
      label: 'Max Travel Distance (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'availability',
      label: 'Availability',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'start_availability',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'service_type',
      label: 'Service Type',
      type: 'select',
      options: [
        { value: 'massage', label: 'Massage Therapy' },
        { value: 'personal_training', label: 'Personal Training' },
        { value: 'yoga', label: 'Yoga' },
        { value: 'nutrition', label: 'Nutrition Counseling' },
        { value: 'therapy', label: 'Therapy' },
        { value: 'beauty', label: 'Beauty Services' },
        { value: 'hair', label: 'Hair Services' },
        { value: 'nails', label: 'Nail Services' },
        { value: 'coaching', label: 'Life Coaching' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'specialization',
      label: 'Specialization',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'session_duration',
      label: 'Session Duration (minutes)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(15),
        Validators.max(480),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'certifications',
      label: 'Certifications',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'education_background',
      label: 'Education Background',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'experience_years',
      label: 'Years of Experience',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'languages_spoken',
      label: 'Languages Spoken',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'home_visits',
      label: 'Home Visits Available',
      type: 'checkbox',
    },
    {
      name: 'online_sessions',
      label: 'Online Sessions Available',
      type: 'checkbox',
    },
    {
      name: 'group_sessions',
      label: 'Group Sessions Available',
      type: 'checkbox',
    },
    {
      name: 'location_has_facility',
      label: 'Have Own Facility',
      type: 'checkbox',
    },
    {
      name: 'target_audience',
      label: 'Target Audience',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'special_needs_experience',
      label: 'Special Needs Experience',
      type: 'checkbox',
    },
  ],

  'events-entertainment': [
    ...baseFields,
    {
      name: 'pricing_type',
      label: 'Pricing Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'hourly', label: 'Hourly Rate' },
        { value: 'daily', label: 'Daily Rate' },
        { value: 'project', label: 'Per Event' },
        { value: 'monthly', label: 'Monthly Rate' },
        { value: 'per_session', label: 'Per Session' },
        { value: 'negotiable', label: 'Negotiable' },
      ],
    },
    {
      name: 'price_min',
      label: 'Minimum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'price_max',
      label: 'Maximum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'remote_available',
      label: 'Virtual Events Available',
      type: 'checkbox',
    },
    {
      name: 'travel_willing',
      label: 'Willing to Travel',
      type: 'checkbox',
    },
    {
      name: 'max_travel_distance',
      label: 'Max Travel Distance (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'availability',
      label: 'Availability',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'start_availability',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'service_type',
      label: 'Service Type',
      type: 'select',
      options: [
        { value: 'dj', label: 'DJ Services' },
        { value: 'musician', label: 'Live Music' },
        { value: 'photographer', label: 'Photography' },
        { value: 'videographer', label: 'Videography' },
        { value: 'event_planning', label: 'Event Planning' },
        { value: 'catering', label: 'Catering' },
        { value: 'entertainment', label: 'Entertainment' },
        { value: 'decoration', label: 'Decoration' },
        { value: 'security', label: 'Security' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'event_types',
      label: 'Event Types',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
      placeholder: 'e.g., Weddings, Corporate events, Birthdays',
    },
    {
      name: 'group_size_min',
      label: 'Minimum Group Size',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.min(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'group_size_max',
      label: 'Maximum Group Size',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.min(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'setup_time_hours',
      label: 'Setup Time (hours)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.min(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'performance_duration_hours',
      label: 'Performance Duration (hours)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0.5),
        Validators.min(100),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'own_equipment',
      label: 'Provide Own Equipment',
      type: 'checkbox',
    },
    {
      name: 'equipment_included',
      label: 'Equipment Included',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'additional_services',
      label: 'Additional Services',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'experience_years',
      label: 'Years of Experience',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'portfolio_url',
      label: 'Portfolio URL',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'references_available',
      label: 'References Available',
      type: 'checkbox',
    },
    {
      name: 'advance_booking_days',
      label: 'Advance Booking Required (days)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(365),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'weekend_available',
      label: 'Weekend Availability',
      type: 'checkbox',
    },
    {
      name: 'travel_distance_max',
      label: 'Maximum Travel Distance (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
  ],

  'education-training': [
    ...baseFields,
    {
      name: 'pricing_type',
      label: 'Pricing Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'hourly', label: 'Hourly Rate' },
        { value: 'daily', label: 'Daily Rate' },
        { value: 'project', label: 'Per Course' },
        { value: 'monthly', label: 'Monthly Rate' },
        { value: 'per_session', label: 'Per Session' },
        { value: 'negotiable', label: 'Negotiable' },
      ],
    },
    {
      name: 'price_min',
      label: 'Minimum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'price_max',
      label: 'Maximum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'remote_available',
      label: 'Online Lessons Available',
      type: 'checkbox',
    },
    {
      name: 'travel_willing',
      label: 'Willing to Travel',
      type: 'checkbox',
    },
    {
      name: 'max_travel_distance',
      label: 'Max Travel Distance (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'availability',
      label: 'Availability',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'start_availability',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'service_type',
      label: 'Service Type',
      type: 'select',
      options: [
        { value: 'tutoring', label: 'Academic Tutoring' },
        { value: 'language_teaching', label: 'Language Teaching' },
        { value: 'music_lessons', label: 'Music Lessons' },
        { value: 'sports_coaching', label: 'Sports Coaching' },
        { value: 'computer_training', label: 'Computer Training' },
        { value: 'driving_lessons', label: 'Driving Lessons' },
        { value: 'skill_training', label: 'Skill Training' },
        { value: 'exam_prep', label: 'Exam Preparation' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'subject_area',
      label: 'Subject Area',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'age_groups',
      label: 'Age Groups',
      type: 'text',
      validators: [Validators.maxLength(255)],
      placeholder: 'e.g., Children (6-12), Teenagers (13-18), Adults',
    },
    {
      name: 'skill_levels',
      label: 'Skill Levels',
      type: 'text',
      validators: [Validators.maxLength(255)],
      placeholder: 'e.g., Beginner, Intermediate, Advanced',
    },
    {
      name: 'lesson_duration',
      label: 'Lesson Duration (minutes)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(15),
        Validators.max(480),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'education_background',
      label: 'Education Background',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'certifications',
      label: 'Certifications',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'teaching_experience_years',
      label: 'Years of Teaching Experience',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'languages_taught',
      label: 'Languages Taught/Spoken',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'online_lessons',
      label: 'Online Lessons Available',
      type: 'checkbox',
    },
    {
      name: 'home_visits',
      label: 'Home Visits Available',
      type: 'checkbox',
    },
    {
      name: 'group_lessons',
      label: 'Group Lessons Available',
      type: 'checkbox',
    },
    {
      name: 'individual_lessons',
      label: 'Individual Lessons Available',
      type: 'checkbox',
    },
    {
      name: 'materials_provided',
      label: 'Learning Materials Provided',
      type: 'checkbox',
    },
  ],

  'transport-delivery': [
    ...baseFields,
    {
      name: 'pricing_type',
      label: 'Pricing Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'per_km', label: 'Per Kilometer' },
        { value: 'per_hour', label: 'Per Hour' },
        { value: 'flat_rate', label: 'Flat Rate' },
        { value: 'per_item', label: 'Per Item' },
        { value: 'negotiable', label: 'Negotiable' },
      ],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'remote_available',
      label: 'Remote Coordination Available',
      type: 'checkbox',
    },
    {
      name: 'travel_willing',
      label: 'Long Distance Available',
      type: 'checkbox',
    },
    {
      name: 'max_travel_distance',
      label: 'Max Service Distance (km)',
      type: 'number',
      validators: [Validators.min(0), Validators.max(1000)],
    },
    {
      name: 'availability',
      label: 'Availability',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'start_availability',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'service_type',
      label: 'Service Type',
      type: 'select',
      options: [
        { value: 'courier', label: 'Courier Service' },
        { value: 'moving', label: 'Moving Service' },
        { value: 'furniture_delivery', label: 'Furniture Delivery' },
        { value: 'shopping_delivery', label: 'Shopping Delivery' },
        { value: 'pickup_service', label: 'Pickup Service' },
        { value: 'taxi', label: 'Taxi Service' },
        { value: 'rideshare', label: 'Rideshare' },
        { value: 'freight', label: 'Freight Transport' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'vehicle_type',
      label: 'Vehicle Type',
      type: 'select',
      options: [
        { value: 'car', label: 'Car' },
        { value: 'van', label: 'Van' },
        { value: 'truck', label: 'Truck' },
        { value: 'motorcycle', label: 'Motorcycle' },
        { value: 'bicycle', label: 'Bicycle' },
        { value: 'on_foot', label: 'On Foot' },
      ],
    },
    {
      name: 'max_weight_kg',
      label: 'Maximum Weight (kg)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(50000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'max_dimensions',
      label: 'Maximum Dimensions',
      type: 'text',
      validators: [Validators.maxLength(50)],
      placeholder: 'e.g., 2m x 1.5m x 1m',
    },
    {
      name: 'passenger_capacity',
      label: 'Passenger Capacity',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(20),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'base_price',
      label: 'Base Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0), Validators.max(100000000)],
    },
    {
      name: 'price_per_km',
      label: 'Price per km (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0), Validators.max(10000)],
    },
    {
      name: 'price_per_hour',
      label: 'Price per hour (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0), Validators.max(10000)],
    },
    {
      name: 'service_radius',
      label: 'Service Radius (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(2000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'max_distance',
      label: 'Maximum Distance (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(1),
        Validators.max(5000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'international_available',
      label: 'International Service Available',
      type: 'checkbox',
    },
    {
      name: 'loading_unloading',
      label: 'Loading/Unloading Service',
      type: 'checkbox',
    },
    {
      name: 'packaging_service',
      label: 'Packaging Service',
      type: 'checkbox',
    },
    {
      name: 'assembly_disassembly',
      label: 'Assembly/Disassembly Service',
      type: 'checkbox',
    },
    {
      name: 'insurance_provided',
      label: 'Insurance Provided',
      type: 'checkbox',
    },
    {
      name: 'commercial_license',
      label: 'Commercial License',
      type: 'checkbox',
    },
    {
      name: 'own_vehicle',
      label: 'Own Vehicle',
      type: 'checkbox',
    },
    {
      name: 'helpers_available',
      label: 'Number of Helpers Available',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(10),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'same_day_service',
      label: 'Same Day Service Available',
      type: 'checkbox',
    },
    {
      name: 'weekend_available',
      label: 'Weekend Service Available',
      type: 'checkbox',
    },
  ],

  professional: [
    ...baseFields,
    {
      name: 'pricing_type',
      label: 'Pricing Type',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'hourly', label: 'Hourly Rate' },
        { value: 'daily', label: 'Daily Rate' },
        { value: 'project', label: 'Project-based' },
        { value: 'monthly', label: 'Monthly Rate' },
        { value: 'per_session', label: 'Per Session' },
        { value: 'negotiable', label: 'Negotiable' },
      ],
    },
    {
      name: 'price_min',
      label: 'Minimum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'price_max',
      label: 'Maximum Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(1), Validators.max(100000.0)],
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
    },
    {
      name: 'remote_available',
      label: 'Remote Work Available',
      type: 'checkbox',
    },
    {
      name: 'travel_willing',
      label: 'Willing to Travel',
      type: 'checkbox',
    },
    {
      name: 'max_travel_distance',
      label: 'Max Travel Distance (km)',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(1000),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'availability',
      label: 'Availability',
      type: 'text',
      validators: [Validators.maxLength(50)],
      placeholder: 'e.g., Weekdays, Evenings, Weekends',
    },
    {
      name: 'start_availability',
      label: 'Available From',
      type: 'date',
    },
    {
      name: 'service_type',
      label: 'Service Type',
      type: 'select',
      options: [
        { value: 'consulting', label: 'Consulting' },
        { value: 'accounting', label: 'Accounting' },
        { value: 'legal', label: 'Legal Services' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'design', label: 'Design' },
        { value: 'programming', label: 'Programming' },
        { value: 'writing', label: 'Writing' },
        { value: 'translation', label: 'Translation' },
        { value: 'project_management', label: 'Project Management' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'service_category',
      label: 'Service Category',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'experience_years',
      label: 'Years of Experience',
      type: 'number',
      step: 1,
      pattern: '^[0-9]+$',
      validators: [
        Validators.min(0),
        Validators.max(50),
        integerOnlyValidator,
        Validators.pattern(/^[0-9]+$/),
      ],
    },
    {
      name: 'education_level',
      label: 'Education Level',
      type: 'select',
      options: [
        { value: 'high_school', label: 'High School' },
        { value: 'apprenticeship', label: 'Apprenticeship' },
        { value: 'bachelor', label: "Bachelor's Degree" },
        { value: 'master', label: "Master's Degree" },
        { value: 'phd', label: 'PhD' },
        { value: 'professional_cert', label: 'Professional Certification' },
      ],
    },
    {
      name: 'certifications',
      label: 'Certifications',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'languages_spoken',
      label: 'Languages Spoken',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'portfolio_url',
      label: 'Portfolio URL',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'sample_work',
      label: 'Sample Work Description',
      type: 'textarea',
      rows: 4,
      validators: [Validators.maxLength(1000)],
    },
  ],
};

export const marketplaceFormConfigs: CategoryFormConfig = {
  home: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'item_category',
      label: 'Item Category',
      type: 'select',
      options: [
        { value: 'furniture', label: 'Furniture' },
        { value: 'appliances', label: 'Appliances' },
        { value: 'decor', label: 'Decor' },
        { value: 'garden', label: 'Garden' },
        { value: 'tools', label: 'Tools' },
        { value: 'lighting', label: 'Lighting' },
        { value: 'textiles', label: 'Textiles' },
        { value: 'kitchen', label: 'Kitchen' },
        { value: 'bathroom', label: 'Bathroom' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'dimensions',
      label: 'Dimensions',
      type: 'text',
      validators: [Validators.maxLength(100)],
      placeholder: 'e.g., 120x80x40 cm',
    },
    {
      name: 'weight_kg',
      label: 'Weight (kg)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0.1)],
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1950)],
    },
    {
      name: 'original_price',
      label: 'Original Purchase Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0)],
    },
    {
      name: 'warranty_remaining',
      label: 'Warranty Remaining',
      type: 'checkbox',
    },
  ],

  electronics: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: [
        { value: 'computer', label: 'Computer' },
        { value: 'laptop', label: 'Laptop' },
        { value: 'tablet', label: 'Tablet' },
        { value: 'tv', label: 'TV' },
        { value: 'audio', label: 'Audio Equipment' },
        { value: 'gaming', label: 'Gaming' },
        { value: 'camera', label: 'Camera' },
        { value: 'smart_home', label: 'Smart Home' },
        { value: 'wearable', label: 'Wearable' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'power_consumption',
      label: 'Power Consumption',
      type: 'text',
      validators: [Validators.maxLength(20)],
      placeholder: 'e.g., 65W, 150W',
    },
    {
      name: 'connectivity',
      label: 'Connectivity',
      type: 'text',
      validators: [Validators.maxLength(255)],
      placeholder: 'e.g., WiFi, Bluetooth, USB-C',
    },
    {
      name: 'original_packaging',
      label: 'Original Packaging',
      type: 'checkbox',
    },
    {
      name: 'manual_included',
      label: 'Manual Included',
      type: 'checkbox',
    },
    {
      name: 'accessories_included',
      label: 'Accessories Included',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'cables_included',
      label: 'Cables Included',
      type: 'textarea',
      rows: 2,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1980)],
    },
    {
      name: 'purchase_price',
      label: 'Original Purchase Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0)],
    },
    {
      name: 'warranty_remaining',
      label: 'Warranty Remaining',
      type: 'checkbox',
    },
    {
      name: 'warranty_months',
      label: 'Warranty Duration (months)',
      type: 'number',
      validators: [Validators.min(0), Validators.max(120)],
    },
  ],

  sport: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'sport_category',
      label: 'Sport Category',
      type: 'select',
      options: [
        { value: 'fitness', label: 'Fitness' },
        { value: 'cycling', label: 'Cycling' },
        { value: 'football', label: 'Football' },
        { value: 'tennis', label: 'Tennis' },
        { value: 'golf', label: 'Golf' },
        { value: 'swimming', label: 'Swimming' },
        { value: 'skiing', label: 'Skiing' },
        { value: 'basketball', label: 'Basketball' },
        { value: 'running', label: 'Running' },
        { value: 'outdoor', label: 'Outdoor Activities' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'item_type',
      label: 'Item Type',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'size_info',
      label: 'Size/Measurements',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'usage_level',
      label: 'Usage Level',
      type: 'select',
      options: [
        { value: 'never_used', label: 'Never Used' },
        { value: 'light_use', label: 'Light Use' },
        { value: 'moderate_use', label: 'Moderate Use' },
        { value: 'heavy_use', label: 'Heavy Use' },
      ],
    },
    {
      name: 'suitable_for',
      label: 'Suitable For',
      type: 'select',
      options: [
        { value: 'beginner', label: 'Beginner' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'advanced', label: 'Advanced' },
        { value: 'professional', label: 'Professional' },
        { value: 'all', label: 'All Levels' },
      ],
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1950)],
    },
    {
      name: 'original_price',
      label: 'Original Purchase Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0)],
    },
  ],

  children: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'item_category',
      label: 'Item Category',
      type: 'select',
      options: [
        { value: 'toys', label: 'Toys' },
        { value: 'clothing', label: 'Clothing' },
        { value: 'shoes', label: 'Shoes' },
        { value: 'books', label: 'Books' },
        { value: 'games', label: 'Games' },
        { value: 'furniture', label: 'Furniture' },
        { value: 'stroller', label: 'Stroller' },
        { value: 'car_seat', label: 'Car Seat' },
        { value: 'feeding', label: 'Feeding' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'age_group',
      label: 'Age Group',
      type: 'select',
      options: [
        { value: '0-6_months', label: '0-6 months' },
        { value: '6-12_months', label: '6-12 months' },
        { value: '1-2_years', label: '1-2 years' },
        { value: '3-5_years', label: '3-5 years' },
        { value: '6-8_years', label: '6-8 years' },
        { value: '9-12_years', label: '9-12 years' },
        { value: 'teen', label: 'Teen' },
      ],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'size_info',
      label: 'Size',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'safety_standards',
      label: 'Safety Standards',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'missing_parts',
      label: 'Missing Parts',
      type: 'checkbox',
    },
    {
      name: 'original_packaging',
      label: 'Original Packaging',
      type: 'checkbox',
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1990)],
    },
    {
      name: 'original_price',
      label: 'Original Purchase Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0)],
    },
  ],

  'head-to-toe': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'item_type',
      label: 'Item Type',
      type: 'select',
      options: [
        { value: 'shirt', label: 'Shirt' },
        { value: 'pants', label: 'Pants' },
        { value: 'dress', label: 'Dress' },
        { value: 'jacket', label: 'Jacket' },
        { value: 'shoes', label: 'Shoes' },
        { value: 'accessories', label: 'Accessories' },
        { value: 'underwear', label: 'Underwear' },
        { value: 'sportswear', label: 'Sportswear' },
        { value: 'formal', label: 'Formal' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: [
        { value: 'mens', label: "Men's" },
        { value: 'womens', label: "Women's" },
        { value: 'unisex', label: 'Unisex' },
        { value: 'kids', label: 'Kids' },
      ],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'size_info',
      label: 'Size',
      type: 'text',
      validators: [Validators.maxLength(30)],
    },
    {
      name: 'measurements',
      label: 'Measurements',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'fit_type',
      label: 'Fit Type',
      type: 'select',
      options: [
        { value: 'slim', label: 'Slim' },
        { value: 'regular', label: 'Regular' },
        { value: 'loose', label: 'Loose' },
        { value: 'oversized', label: 'Oversized' },
      ],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'pattern',
      label: 'Pattern',
      type: 'text',
      validators: [Validators.maxLength(30)],
    },
    {
      name: 'care_instructions',
      label: 'Care Instructions',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'alterations_made',
      label: 'Alterations Made',
      type: 'checkbox',
    },
    {
      name: 'season',
      label: 'Season',
      type: 'select',
      options: [
        { value: 'spring', label: 'Spring' },
        { value: 'summer', label: 'Summer' },
        { value: 'fall', label: 'Fall' },
        { value: 'winter', label: 'Winter' },
        { value: 'all_season', label: 'All Season' },
      ],
    },
    {
      name: 'style',
      label: 'Style',
      type: 'text',
      validators: [Validators.maxLength(30)],
    },
    {
      name: 'original_price',
      label: 'Original Purchase Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0)],
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1950)],
    },
  ],

  'machines-tools': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'tool_category',
      label: 'Tool Category',
      type: 'select',
      options: [
        { value: 'power_tools', label: 'Power Tools' },
        { value: 'hand_tools', label: 'Hand Tools' },
        { value: 'garden_tools', label: 'Garden Tools' },
        { value: 'workshop', label: 'Workshop' },
        { value: 'construction', label: 'Construction' },
        { value: 'automotive', label: 'Automotive' },
        { value: 'measuring', label: 'Measuring' },
        { value: 'safety', label: 'Safety' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'tool_type',
      label: 'Tool Type',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'power_type',
      label: 'Power Type',
      type: 'select',
      options: [
        { value: 'corded_electric', label: 'Corded Electric' },
        { value: 'battery', label: 'Battery' },
        { value: 'pneumatic', label: 'Pneumatic' },
        { value: 'manual', label: 'Manual' },
        { value: 'gas_powered', label: 'Gas Powered' },
      ],
    },
    {
      name: 'voltage',
      label: 'Voltage',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'power_consumption',
      label: 'Power Consumption',
      type: 'text',
      validators: [Validators.maxLength(30)],
    },
    {
      name: 'usage_level',
      label: 'Usage Level',
      type: 'select',
      options: [
        { value: 'never_used', label: 'Never Used' },
        { value: 'light_use', label: 'Light Use' },
        { value: 'moderate_use', label: 'Moderate Use' },
        { value: 'heavy_use', label: 'Heavy Use' },
        { value: 'professional_use', label: 'Professional Use' },
      ],
    },
    {
      name: 'original_case',
      label: 'Original Case',
      type: 'checkbox',
    },
    {
      name: 'manual_included',
      label: 'Manual Included',
      type: 'checkbox',
    },
    {
      name: 'accessories_included',
      label: 'Accessories Included',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'spare_parts_included',
      label: 'Spare Parts Included',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1950)],
    },
    {
      name: 'warranty_remaining',
      label: 'Warranty Remaining',
      type: 'checkbox',
    },
  ],

  media: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'media_type',
      label: 'Media Type',
      type: 'select',
      options: [
        { value: 'dvd', label: 'DVD' },
        { value: 'blu_ray', label: 'Blu-ray' },
        { value: '4k_uhd', label: '4K UHD' },
        { value: 'cd', label: 'CD' },
        { value: 'vinyl', label: 'Vinyl' },
        { value: 'cassette', label: 'Cassette' },
        { value: 'vhs', label: 'VHS' },
        { value: 'game', label: 'Game' },
        { value: 'software', label: 'Software' },
        { value: 'digital', label: 'Digital' },
      ],
    },
    {
      name: 'genre',
      label: 'Genre',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'format',
      label: 'Format',
      type: 'text',
      validators: [Validators.maxLength(30)],
    },
    {
      name: 'title_original',
      label: 'Original Title',
      type: 'text',
      validators: [Validators.maxLength(255)],
    },
    {
      name: 'artist_author',
      label: 'Artist/Author',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'release_year',
      label: 'Release Year',
      type: 'number',
      validators: [Validators.min(1900)],
    },
    {
      name: 'language',
      label: 'Language',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'runtime_minutes',
      label: 'Runtime (minutes)',
      type: 'number',
      validators: [Validators.min(1)],
    },
    {
      name: 'case_condition',
      label: 'Case Condition',
      type: 'select',
      options: [
        { value: 'mint', label: 'Mint' },
        { value: 'good', label: 'Good' },
        { value: 'damaged', label: 'Damaged' },
        { value: 'missing', label: 'Missing' },
      ],
    },
    {
      name: 'original_packaging',
      label: 'Original Packaging',
      type: 'checkbox',
    },
    {
      name: 'limited_edition',
      label: 'Limited Edition',
      type: 'checkbox',
    },
    {
      name: 'special_features',
      label: 'Special Features',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'region_code',
      label: 'Region Code',
      type: 'text',
      validators: [Validators.maxLength(10)],
    },
  ],

  'musical-instruments': [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'instrument_category',
      label: 'Instrument Category',
      type: 'select',
      options: [
        { value: 'string', label: 'String' },
        { value: 'wind', label: 'Wind' },
        { value: 'brass', label: 'Brass' },
        { value: 'percussion', label: 'Percussion' },
        { value: 'keyboard', label: 'Keyboard' },
        { value: 'electronic', label: 'Electronic' },
        { value: 'accessory', label: 'Accessory' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'instrument_type',
      label: 'Instrument Type',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'color_finish',
      label: 'Color/Finish',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'size_info',
      label: 'Size',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'year_made',
      label: 'Year Made',
      type: 'number',
      validators: [Validators.min(1800)],
    },
    {
      name: 'country_of_origin',
      label: 'Country of Origin',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'serial_number',
      label: 'Serial Number',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'modifications_made',
      label: 'Modifications Made',
      type: 'checkbox',
    },
    {
      name: 'original_case',
      label: 'Original Case',
      type: 'checkbox',
    },
    {
      name: 'accessories_included',
      label: 'Accessories Included',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1950)],
    },
    {
      name: 'appraised_value',
      label: 'Appraised Value (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0)],
    },
  ],

  literature: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'item_type',
      label: 'Item Type',
      type: 'select',
      options: [
        { value: 'book', label: 'Book' },
        { value: 'magazine', label: 'Magazine' },
        { value: 'journal', label: 'Journal' },
        { value: 'comic', label: 'Comic' },
        { value: 'textbook', label: 'Textbook' },
        { value: 'manual', label: 'Manual' },
        { value: 'encyclopedia', label: 'Encyclopedia' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'title_original',
      label: 'Title',
      type: 'text',
      required: true,
      validators: [Validators.required, Validators.maxLength(255)],
    },
    {
      name: 'author',
      label: 'Author',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'publisher',
      label: 'Publisher',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'publication_year',
      label: 'Publication Year',
      type: 'number',
      validators: [Validators.min(1400)],
    },
    {
      name: 'edition',
      label: 'Edition',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'language',
      label: 'Language',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'pages',
      label: 'Number of Pages',
      type: 'number',
      validators: [Validators.min(1)],
    },
    {
      name: 'isbn',
      label: 'ISBN',
      type: 'text',
      validators: [Validators.maxLength(20)],
    },
    {
      name: 'format_type',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'hardcover', label: 'Hardcover' },
        { value: 'paperback', label: 'Paperback' },
        { value: 'spiral_bound', label: 'Spiral Bound' },
        { value: 'digital', label: 'Digital' },
      ],
    },
    {
      name: 'genre',
      label: 'Genre',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'subject',
      label: 'Subject',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'target_audience',
      label: 'Target Audience',
      type: 'select',
      options: [
        { value: 'children', label: 'Children' },
        { value: 'young_adult', label: 'Young Adult' },
        { value: 'adult', label: 'Adult' },
        { value: 'academic', label: 'Academic' },
        { value: 'professional', label: 'Professional' },
      ],
    },
    {
      name: 'signed_copy',
      label: 'Signed Copy',
      type: 'checkbox',
    },
    {
      name: 'first_edition',
      label: 'First Edition',
      type: 'checkbox',
    },
    {
      name: 'rare_collectible',
      label: 'Rare/Collectible',
      type: 'checkbox',
    },
  ],

  pets: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'item_category',
      label: 'Item Category',
      type: 'select',
      options: [
        { value: 'food', label: 'Food' },
        { value: 'toys', label: 'Toys' },
        { value: 'accessories', label: 'Accessories' },
        { value: 'furniture', label: 'Furniture' },
        { value: 'health_care', label: 'Health Care' },
        { value: 'grooming', label: 'Grooming' },
        { value: 'training', label: 'Training' },
        { value: 'aquarium', label: 'Aquarium' },
        { value: 'cage', label: 'Cage' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'pet_type',
      label: 'Pet Type',
      type: 'select',
      options: [
        { value: 'dog', label: 'Dog' },
        { value: 'cat', label: 'Cat' },
        { value: 'bird', label: 'Bird' },
        { value: 'fish', label: 'Fish' },
        { value: 'rabbit', label: 'Rabbit' },
        { value: 'hamster', label: 'Hamster' },
        { value: 'reptile', label: 'Reptile' },
        { value: 'horse', label: 'Horse' },
        { value: 'universal', label: 'Universal' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'size_info',
      label: 'Size',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'original_packaging',
      label: 'Original Packaging',
      type: 'checkbox',
    },
    {
      name: 'organic_natural',
      label: 'Organic/Natural',
      type: 'checkbox',
    },
    {
      name: 'washable',
      label: 'Washable',
      type: 'checkbox',
    },
    {
      name: 'expiration_date',
      label: 'Expiration Date',
      type: 'date',
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1950)],
    },
  ],

  other: [
    ...baseFields,
    ...basePriceFields,
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'pickup_only',
      label: 'Pickup Only',
      type: 'checkbox',
    },
    {
      name: 'delivery_available',
      label: 'Delivery Available',
      type: 'checkbox',
    },
    {
      name: 'item_category',
      label: 'Item Category',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'model',
      label: 'Model',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'dimensions',
      label: 'Dimensions',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'weight_kg',
      label: 'Weight (kg)',
      type: 'number',
      step: 0.1,
      validators: [Validators.min(0)],
    },
    {
      name: 'age_estimate',
      label: 'Age Estimate',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'purchase_year',
      label: 'Year Purchased',
      type: 'number',
      validators: [Validators.min(1900)],
    },
    {
      name: 'original_price',
      label: 'Original Purchase Price (€)',
      type: 'number',
      step: 0.01,
      validators: [Validators.min(0)],
    },
  ],

  'giving-away': [
    ...baseFields,
    {
      name: 'price',
      label: 'Price',
      type: 'number',
      required: true,
      validators: [Validators.required],
      value: 0,
      hint: 'Price must be 0 for giving away items',
      disabled: true,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [{ value: 'EUR', label: 'EUR (€)' }],
      disabled: true,
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      required: true,
      validators: [Validators.required],
      options: [
        { value: 'new', label: 'New' },
        { value: 'like_new', label: 'Like New' },
        { value: 'good', label: 'Good' },
        { value: 'fair', label: 'Fair' },
        { value: 'poor', label: 'Poor' },
      ],
    },
    {
      name: 'item_category',
      label: 'Item Category',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'brand',
      label: 'Brand',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'dimensions',
      label: 'Dimensions',
      type: 'text',
      validators: [Validators.maxLength(100)],
    },
    {
      name: 'material',
      label: 'Material',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'color',
      label: 'Color',
      type: 'text',
      validators: [Validators.maxLength(50)],
    },
    {
      name: 'reason_giving',
      label: 'Reason for Giving Away',
      type: 'select',
      options: [
        { value: 'moving', label: 'Moving' },
        { value: 'decluttering', label: 'Decluttering' },
        { value: 'upgrade', label: 'Upgrade' },
        { value: 'no_longer_needed', label: 'No Longer Needed' },
        { value: 'other', label: 'Other' },
      ],
    },
    {
      name: 'pickup_required',
      label: 'Pickup Required',
      type: 'checkbox',
    },
    {
      name: 'available_until',
      label: 'Available Until',
      type: 'date',
    },
    {
      name: 'must_pickup_by',
      label: 'Must Pickup By',
      type: 'date',
    },
    {
      name: 'pickup_requirements',
      label: 'Pickup Requirements',
      type: 'textarea',
      rows: 3,
      validators: [Validators.maxLength(500)],
    },
  ],
};

export function getFormConfig(
  category: string,
  subcategory: string
): FormField[] {
  let configs: CategoryFormConfig;

  switch (category) {
    case 'vehicles':
      configs = vehicleFormConfigs;
      break;
    case 'real-estate':
      configs = realEstateFormConfigs;
      break;
    case 'marketplace':
      configs = marketplaceFormConfigs;
      break;
    case 'jobs':
      configs = jobsFormConfigs;
      break;
    default:
      console.warn('Unknown category:', category);
      return [];
  }

  const result = configs[subcategory] || [];

  if (result.length === 0) {
    console.warn(
      'No form configuration found for subcategory:',
      subcategory,
      'in category:',
      category
    );
  }

  return result;
}
